import React, { useState, useEffect, useCallback } from "react";
import useDarkMode from '../../hooks/useDarkMode';
import useThemeColor from "../../hooks/useThemeColor";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import axiosInstance from "../../config/axiosApi/axiosApi";
import APIs from "../../Constants/apis";
import Preloader from "../../components/CustomComponents/preLoad/preLoader";
import FormInput from "../../components/CustomComponents/Input/formInput";
import ToggleSwitch from "../../components/CustomComponents/Switchery/switchery";
import Select from "react-select";
import showErrorToast, {
  showSuccessToast,
} from "../../components/CustomComponents/toast/toast";
import SubscriptionModal from "./subscriptionModal";
import PaymentMethodModal from "./paymentMethodModal";
import ScheduleManager from "../../components/CustomComponents/TimeSchedule/weeklySlots";
import schedule from "../../components/CustomComponents/TimeSchedule/schedule";
import moment from "moment";
import { updateUserData } from "../../app/auth/authSlice";

const convertToScheduleFormatForProfile = (timings) => {
  const days = [
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
    "Sunday",
  ];
  const firstTiming = timings[0] || {};

  // Check if any day has actual timing data
  const hasAnyTiming = days.some(
    (day) => firstTiming[`aT_${day}Opening`] && firstTiming[`aT_${day}Closing`]
  );

  // If no timing data exists, return null to indicate empty schedule
  if (!hasAnyTiming) {
    return null;
  }

  return days.map((day) => ({
    day,
    checked:
      !!firstTiming[`aT_${day}Opening`] && !!firstTiming[`aT_${day}Closing`],
    timeSlots:
      firstTiming[`aT_${day}Opening`] && firstTiming[`aT_${day}Closing`]
        ? [
          {
            from: firstTiming[`aT_${day}Opening`],
            to: firstTiming[`aT_${day}Closing`],
            description:
              firstTiming.type === "Restaurant"
                ? "All Day"
                : firstTiming.type,
          },
        ]
        : [{ from: "00:00", to: "23:59" }], // Default time slot when day is not set (12:00 AM to 11:59 PM)
  }));
};

const formatTimeForDisplay = (time) => {
  if (!time) return "";
  return moment(time, ["HH:mm", "h:mm A"]).format("h:mm A");
};

// Ensure numeric fields are numbers or null (not empty strings). Keep deliveryCharges as string.
const sanitizeSettingsPayload = (state) => {
  const numericFields = [
    "S_ServiceCharges",
    "S_VAT",
    "S_ServicesChargesOnCovers",
    "S_MinOrderAmount",
    "S_DeliveryTime",
    "S_TableReservationMarginTime",
    "S_TableReservedReleaseTime",
    "S_ShowingReserveredTableTime",
    "S_ShowCancelledOrderDuration",
    "S_VisibleCancelOrderDaysinCancelTab",
    "S_PointperPurchase",
    "S_PointSperDollar",
    "PreOrderAlertInMins",
  ];

  const sanitized = { ...state };

  numericFields.forEach((field) => {
    const value = sanitized[field];
    if (value === "" || value === undefined) {
      sanitized[field] = null; // backend expects nullable ints
    } else if (typeof value === "string") {
      const parsed = Number(value);
      sanitized[field] = Number.isNaN(parsed) ? null : parsed;
    }
  });

  // Special handling for S_DeliveryTime to ensure it's always an integer
  if (sanitized.S_DeliveryTime !== null && sanitized.S_DeliveryTime !== undefined) {
    sanitized.S_DeliveryTime = parseInt(sanitized.S_DeliveryTime) || 0;
  }

  // Special handling for VAT when tax is disabled — rate and registration go to 0/null; VAT Type is always preserved
  if (!sanitized.IsTaxEnabled) {
    sanitized.S_VAT = 0;
    sanitized.S_VATRegNo = null;
  }

  // DeliveryCharges must be string according to backend error
  const dc = sanitized.DeliveryCharges;
  if (dc === undefined || dc === null) {
    sanitized.DeliveryCharges = "";
  } else if (typeof dc === "number") {
    sanitized.DeliveryCharges = dc.toFixed(2);
  } else if (typeof dc !== "string") {
    sanitized.DeliveryCharges = String(dc);
  }

  return sanitized;
};

// When VAT is disabled, send rate 0 and registration null; VAT Type is always saved from form state.
const getVATPayloadFromForm = (formState) => {
  const vatType = formState.s_VARType || "inclusive";
  if (!formState.isTaxEnabled) {
    return { S_VAT: 0, S_VATRegNo: null, S_VARType: vatType };
  }
  const sVatRaw = formState.s_VAT;
  const S_VAT = sVatRaw === "" || sVatRaw === null || sVatRaw === undefined ? null : (parseInt(sVatRaw, 10) ?? 0);
  return {
    S_VAT: S_VAT,
    S_VATRegNo: formState.s_VATRegNo || "",
    S_VARType: vatType,
  };
};

function Settings() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const CompID = useSelector((state) => state.auth.user)?.userData?.compID;
  const { settingsData } = useSelector((state) => state.auth);

  // Get app settings from Redux
  const appSettings = useSelector((state) => state.appSettings);
  const { appTheme } = appSettings;

  // Check if dark mode is enabled
  const isDarkMode = useDarkMode();

  // Get theme color from centralized hook
  const themeColor = useThemeColor();

  // Hidden for now - set to false to show again
  const HIDE_TABLE_RESERVATION = true;
  const HIDE_LOYALTY_OPTION = true;
  const HIDE_ORDERS_TRACKING_TV = true;

  const [loading, setLoading] = useState(false);
  const [editingField, setEditingField] = useState(null);
  const [editValue, setEditValue] = useState("");
  const [taxEditing, setTaxEditing] = useState(false);
  const [previousSettingsData, setPreviousSettingsData] = useState(null);
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [isSubscriptionModalOpen, setIsSubscriptionModalOpen] = useState(false);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [paymentMethodToEdit, setPaymentMethodToEdit] = useState(null);
  const [isOnlineOrderingEditing, setIsOnlineOrderingEditing] = useState(false);
  const [onlineOrderingPreviousData, setOnlineOrderingPreviousData] = useState(null);
  const [isGeneralEditing, setIsGeneralEditing] = useState(false);
  const [generalPreviousData, setGeneralPreviousData] = useState(null);
  const [isTableReservationEditing, setIsTableReservationEditing] = useState(false);
  const [tableReservationPreviousData, setTableReservationPreviousData] = useState(null);
  const [isKitchenEditing, setIsKitchenEditing] = useState(false);
  const [kitchenPreviousData, setKitchenPreviousData] = useState(null);
  const [isLoyaltyEditing, setIsLoyaltyEditing] = useState(false);
  const [loyaltyPreviousData, setLoyaltyPreviousData] = useState(null);
  const [isTVAppEditing, setIsTVAppEditing] = useState(false);
  const [tvAppPreviousData, setTVAppPreviousData] = useState(null);
  const [enableSchedule, setEnableSchedule] = useState({
    weeklyServiceHours: false,
  });
  const [schedules, setSchedules] = useState({
    weeklyServiceHours: schedule,
  });
  const [timingData, setTimingData] = useState({
    weeklyServiceHours: [],
  });
  const [formState, setFormState] = useState({
    s_ID: 0,
    CompID: CompID,
    s_VAT: "",
    s_VATRegNo: "",
    s_VARType: "inclusive",
    s_ServiceCharges: 0,
    deliveryCharges: "0",
    s_ServicesChargesOnCovers: "",
    s_MinOrderAmount: "",
    s_DeliveryTime: 0,
    s_ReturnPolicy: "",
    isShowWarningOnDeletingCustomerData: false,
    s_IsUpdatefromKitchen: true,
    manageCoversOnTablePlan: true,
    s_TableReservationMarginTime: 0,
    s_TableReservedReleaseTime: 0,
    s_ShowingReserveredTableTime: 0,
    s_ShowCancelledOrderDuration: 0,
    s_VisibleCancelOrderDaysinCancelTab: 0,
    s_IsHaveLoyalty: false,
    s_PointperPurchase: 0,
    s_PointSperDollar: 0,
    tV_ISOrderStatus: false,
    tV_ISOrderType: false,
    tV_ISOrderNumber: false,
    tV_ISOrderOwner: false,
    isActivePreOrderModule: false,
    preOrderingCollection: false,
    preOrderingDelivery: false,
    preOrderDayRange: 0,
    preOrderAlertInMins: 20,
    compDeliveryStatus: false,
    compCollectionStatus: false,
    isTaxEnabled: false,
    SubscriptionPlan: "ALTepos for Restaurant Free",
    LocationCount: 1,
    KioskCount: 0,
    SubscriptionEndDate: "29 March 2025",
    SubscriptionPlanName: "ALTepos for Restaurant Plus",
  });

  const preOrderOptions = [
    { value: null, label: "Select Limit" },
    { value: 0, label: "No Limit" },
    { value: 1, label: "1 Day" },
    { value: 2, label: "2 Days" },
    { value: 3, label: "3 Days" },
    { value: 4, label: "4 Days" },
    { value: 5, label: "5 Days" },
    { value: 6, label: "6 Days" },
    { value: 7, label: "7 Days" },
    { value: 8, label: "8 Days" },
    { value: 9, label: "9 Days" },
    { value: 10, label: "10 Days" },
    { value: 11, label: "11 Days" },
    { value: 12, label: "12 Days" },
    { value: 13, label: "13 Days" },
    { value: 14, label: "14 Days" },
    { value: 15, label: "15 Days" },
    { value: 16, label: "16 Days" },
    { value: 17, label: "17 Days" },
    { value: 18, label: "18 Days" },
    { value: 19, label: "19 Days" },
    { value: 20, label: "20 Days" },
    { value: 21, label: "21 Days" },
    { value: 22, label: "22 Days" },
    { value: 23, label: "23 Days" },
    { value: 24, label: "24 Days" },
    { value: 25, label: "25 Days" },
    { value: 26, label: "26 Days" },
    { value: 27, label: "27 Days" },
    { value: 28, label: "28 Days" },
    { value: 29, label: "29 Days" },
    { value: 30, label: "30 Days" },
  ];

  const fetchSettings = useCallback(async () => {
    try {
      setLoading(true);
      // Fetch fresh data from API
      const response = await axiosInstance.get(`${APIs.GET_COMPANY_SETTINGS}`);
      const data = response.data.data;

      // Update Redux state with fresh data
      dispatch(updateUserData({
        settingsData: data,
        lastDataRefresh: new Date().toISOString()
      }));

      // Restaurant Hours only (Delivery/Collection schedules unified on Restaurant)
      const restaurantTimings =
        data.timings?.filter((t) => t.type === "Restaurant") || [];
      const newTimingData = { weeklyServiceHours: restaurantTimings };
      if (restaurantTimings.length > 0) {
        const convertedSchedules =
          convertToScheduleFormatForProfile(restaurantTimings);
        if (convertedSchedules) {
          setEnableSchedule({ weeklyServiceHours: true });
          setSchedules({ weeklyServiceHours: convertedSchedules });
        } else {
          setEnableSchedule({ weeklyServiceHours: false });
          setSchedules({ weeklyServiceHours: schedule });
        }
      } else {
        setEnableSchedule({ weeklyServiceHours: false });
        setSchedules({ weeklyServiceHours: schedule });
      }

      setTimingData(newTimingData);

      setFormState((prev) => ({
        ...prev,
        ...data,
        s_ID: data.s_ID,
        CompID: data.CompID || CompID,
        s_VAT: data.s_VAT || "",
        s_VATRegNo: data.s_VATRegNo || "",
        s_VARType: data.s_VARType || "inclusive",
        s_ServiceCharges: data.s_ServiceCharges || 0,
        deliveryCharges: String(data.deliveryCharges || "0"),
        s_ServicesChargesOnCovers: data.s_ServicesChargesOnCovers || "",
        s_MinOrderAmount: data.s_MinOrderAmount || "",
        s_DeliveryTime: data.s_DeliveryTime || 0,
        s_ReturnPolicy: data.s_ReturnPolicy || "",
        isShowWarningOnDeletingCustomerData:
          !!data.isShowWarningOnDeletingCustomerData,
        s_IsUpdatefromKitchen: !!data.s_IsUpdatefromKitchen,
        manageCoversOnTablePlan: !!data.manageCoversOnTablePlan,
        s_TableReservationMarginTime: data.s_TableReservationMarginTime || 0,
        s_TableReservedReleaseTime: data.s_TableReservedRelesaeTime || 0,
        s_ShowingReserveredTableTime: data.s_ShowingReserveredTableTime || 0,
        s_ShowCancelledOrderDuration: data.s_ShowCancelledOrderDuration || 0,
        s_VisibleCancelOrderDaysinCancelTab:
          data.s_VisibleCancelOrderDaysinCancelTab || 0,
        s_IsHaveLoyalty: !!data.s_IsHaveLoyalty,
        s_PointperPurchase: data.s_PointperPurchase || 0,
        s_PointSperDollar: data.s_PointSperDollar || 0,
        tV_ISOrderStatus: !!data.tV_ISOrderStatus,
        tV_ISOrderType: !!data.tV_ISOrderType,
        tV_ISOrderNumber: !!data.tV_ISOrderNumber,
        tV_ISOrderOwner: !!data.tV_ISOrderOwner,
        isActivePreOrderModule: !!data.isActivePreOrderModule,
        preOrderingCollection: !!data.preOrderingCollection,
        preOrderingDelivery: !!data.preOrderingDelivery,
        preOrderDayRange: data.preOrderDayRange || 0,
        preOrderAlertInMins: data.preOrderAlertInMins || 20,
        compDeliveryStatus: !!data.compDeliveryStatus,
        compCollectionStatus: !!data.compCollectionStatus,
        isTaxEnabled: data.isTaxEnabled ?? false,
        SubscriptionPlan:
          data.SubscriptionPlan,
        LocationCount: data.LocationCount || 1,
        KioskCount: data.KioskCount || 0,
        SubscriptionEndDate: data.SubscriptionEndDate,
        SubscriptionPlanName:
          data.SubscriptionPlanName,
      }));
    } catch (err) {
      showErrorToast("Failed to load settings");
    } finally {
      setLoading(false);
    }
  }, [dispatch]);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  useEffect(() => {
    const getElements = () => ({
      sections: document.querySelectorAll("#bsSpyContent > div"),
      navLinks: document.querySelectorAll("#bsSpyTarget > a"),
    });

    let { sections, navLinks } = getElements();

    if (!sections.length || !navLinks.length) {
      return;
    }

    function activateNavLink(id) {
      navLinks.forEach((link) => {
        link.classList.remove("active");
        // Reset to inactive styling
        link.style.color = isDarkMode ? '#d1d5db' : '#6b7280';
        link.style.borderLeft = '3px solid transparent';
      });
      const target = document.querySelector(`#bsSpyTarget a[href="#${id}"]`);
      if (target) {
        target.classList.add("active");
        // Apply active styling with theme color
        target.style.color = themeColor;
        target.style.borderLeft = `3px solid ${themeColor}`;
      }
    }

    function handleScroll() {
      sections = getElements().sections;

      let activeSectionId = null;
      const scrollPosition = window.scrollY + 100; // Offset for better detection
      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;

      // Check if we're near the bottom of the page
      const isNearBottom = (window.scrollY + windowHeight) >= (documentHeight - 50);

      // Find the section that's currently in view
      for (let i = sections.length - 1; i >= 0; i--) {
        const section = sections[i];
        const rect = section.getBoundingClientRect();
        const sectionTop = section.offsetTop;
        const sectionHeight = section.offsetHeight;

        // Check if the scroll position is within this section
        if (scrollPosition >= sectionTop && scrollPosition < sectionTop + sectionHeight) {
          activeSectionId = section.getAttribute("id");
          break;
        }
      }

      // If no section is found, check if we're at the top
      if (!activeSectionId && window.scrollY < 100) {
        activeSectionId = sections[0]?.getAttribute("id");
      }

      // If we're near the bottom of the page, highlight the last section
      if (!activeSectionId && isNearBottom) {
        activeSectionId = sections[sections.length - 1]?.getAttribute("id");
      }

      // Additional check: if we're at the very bottom, ensure last section is highlighted
      if (!activeSectionId) {
        const lastSection = sections[sections.length - 1];
        if (lastSection) {
          const lastRect = lastSection.getBoundingClientRect();
          // If the last section is visible or we're past it, highlight it
          if (lastRect.top <= windowHeight || window.scrollY + windowHeight >= documentHeight - 10) {
            activeSectionId = lastSection.getAttribute("id");
          }
        }
      }

      if (activeSectionId) {
        activateNavLink(activeSectionId);
      }
    }

    let scrollTimeout;
    const throttleScroll = () => {
      if (!scrollTimeout) {
        scrollTimeout = setTimeout(() => {
          handleScroll();
          scrollTimeout = null;
        }, 10); // Reduced timeout for more responsive updates
      }
    };

    // Add immediate scroll handler for better responsiveness
    window.addEventListener("scroll", handleScroll);
    window.addEventListener("scroll", throttleScroll);
    handleScroll();

    return () => {
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("scroll", throttleScroll);
      clearTimeout(scrollTimeout);
    };
  }, []);

  const handleInputChange = useCallback((e, isDropdown = false) => {
    const value = isDropdown
      ? e.value
      : e.target?.type === "checkbox"
        ? e.target.checked
        : e.target?.value;
    const name = isDropdown ? e.name : e.target?.name;

    // Handle checkbox values properly
    if (e.target?.type === "checkbox") {
      setFormState((prev) => ({
        ...prev,
        [name]: e.target.checked,
      }));
      return;
    }

    // Special handling for deliveryCharges - keep as string
    if (name === "deliveryCharges") {
      const numValue = Number(value);
      if (value !== "" && (isNaN(numValue) || numValue < 0)) {
        showErrorToast("Delivery Charges must be a positive number");
        return;
      }
      const stringValue = value === "" ? "" : String(value);
      setEditValue(stringValue);
      setFormState((prev) => ({
        ...prev,
        [name]: stringValue,
      }));
      return;
    }

    const sanitizedValue = [
      "s_ServiceCharges",
      "s_VAT",
      "s_ServicesChargesOnCovers",
      "s_MinOrderAmount",
      "s_DeliveryTime",
      "s_TableReservationMarginTime",
      "s_TableReservedReleaseTime",
      "s_ShowingReserveredTableTime",
      "s_ShowCancelledOrderDuration",
      "s_VisibleCancelOrderDaysinCancelTab",
      "s_PointperPurchase",
      "s_PointSperDollar",
      "preOrderAlertInMins",
    ].includes(name)
      ? isNaN(Number(value)) || value === ""
        ? ""
        : Number(value)
      : value;

    if (typeof sanitizedValue === "number" && sanitizedValue < 0) {
      showErrorToast(
        `${name.replace(/([A-Z])/g, " $1").trim()} must be a positive number`
      );
      return;
    }

    setEditValue(sanitizedValue);
    setFormState((prev) => ({
      ...prev,
      [name]: sanitizedValue,
    }));
  }, []);

  const handleSelectChange = (name, option) => {
    handleInputChange({ name, value: option ? option.value : "" }, true);
  };

  const handleToggleChange = async (name, checked) => {
    try {
      await saveField(name, checked);
    } catch (error) {
      showErrorToast(`Failed to save ${name}`);
    }
  };

  const startEditing = (name, value) => {
    if (name === "Tax") {
      setTaxEditing(true);
      setPreviousSettingsData({ ...formState });
    } else {
      setEditingField(name);
      setEditValue(value);
      setPreviousSettingsData({ ...formState });

      // Handle Restaurant Hours timing field
      if (name === "weeklyServiceHours") {
        const hasTimingData =
          value.length > 0 &&
          value[0] &&
          (value[0].aT_MondayOpening ||
            value[0].aT_TuesdayOpening ||
            value[0].aT_WednesdayOpening ||
            value[0].aT_ThursdayOpening ||
            value[0].aT_FridayOpening ||
            value[0].aT_SaturdayOpening ||
            value[0].aT_SundayOpening);
        setEnableSchedule((prev) => ({ ...prev, [name]: hasTimingData }));
      }
    }
  };

  const cancelEditing = () => {
    if (previousSettingsData) {
      setFormState(previousSettingsData);
    }
    setEditingField(null);
    setTaxEditing(false);
    setEditValue("");
    setPreviousSettingsData(null);
  };

  const cancelEditingAfterSubmit = () => {
    setEditingField(null);
    setTaxEditing(false);
    setEditValue("");
    setPreviousSettingsData(null);
  };

  const startOnlineOrderingEditing = () => {
    setIsOnlineOrderingEditing(true);
    setOnlineOrderingPreviousData({
      isActivePreOrderModule: formState.isActivePreOrderModule,
      preOrderingCollection: formState.preOrderingCollection,
      preOrderingDelivery: formState.preOrderingDelivery,
      preOrderDayRange: formState.preOrderDayRange,
      preOrderAlertInMins: formState.preOrderAlertInMins,
      compDeliveryStatus: formState.compDeliveryStatus,
      compCollectionStatus: formState.compCollectionStatus,
    });
  };

  const cancelOnlineOrderingEditing = () => {
    if (onlineOrderingPreviousData) {
      setFormState((prev) => ({
        ...prev,
        ...onlineOrderingPreviousData,
      }));
    }
    setIsOnlineOrderingEditing(false);
    setOnlineOrderingPreviousData(null);
  };

  const startGeneralEditing = () => {
    setIsGeneralEditing(true);
    setGeneralPreviousData({
      s_ServiceCharges: formState.s_ServiceCharges,
      deliveryCharges: formState.deliveryCharges,
      s_MinOrderAmount: formState.s_MinOrderAmount,
      s_DeliveryTime: formState.s_DeliveryTime,
      s_ReturnPolicy: formState.s_ReturnPolicy,
      isShowWarningOnDeletingCustomerData: formState.isShowWarningOnDeletingCustomerData,
      manageCoversOnTablePlan: formState.manageCoversOnTablePlan,
    });
  };

  const cancelGeneralEditing = () => {
    if (generalPreviousData) {
      setFormState((prev) => ({
        ...prev,
        ...generalPreviousData,
      }));
    }
    setIsGeneralEditing(false);
    setGeneralPreviousData(null);
  };

  const startTableReservationEditing = () => {
    setIsTableReservationEditing(true);
    setTableReservationPreviousData({
      s_TableReservationMarginTime: formState.s_TableReservationMarginTime,
      s_TableReservedReleaseTime: formState.s_TableReservedReleaseTime,
      s_ShowingReserveredTableTime: formState.s_ShowingReserveredTableTime,
    });
  };

  const cancelTableReservationEditing = () => {
    if (tableReservationPreviousData) {
      setFormState((prev) => ({
        ...prev,
        ...tableReservationPreviousData,
      }));
    }
    setIsTableReservationEditing(false);
    setTableReservationPreviousData(null);
  };

  const saveTableReservationSettings = async () => {
    try {
      setLoading(true);

      const updatedSettings = {
        S_ID: formState.s_ID || 0,
        CompID: formState.CompID,
        // Update only Table Reservation fields
        S_TableReservationMarginTime: parseInt(formState.s_TableReservationMarginTime) || 0,
        S_TableReservedReleaseTime: parseInt(formState.s_TableReservedReleaseTime) || 0,
        S_ShowingReserveredTableTime: parseInt(formState.s_ShowingReserveredTableTime) || 0,
        // Keep all other fields unchanged
        S_ServiceCharges: parseInt(formState.s_ServiceCharges) || 0,
        DeliveryCharges: String(formState.deliveryCharges || "0"),
        S_MinOrderAmount: parseFloat(formState.s_MinOrderAmount) || 0,
        S_DeliveryTime: parseInt(formState.s_DeliveryTime) || 0,
        S_ReturnPolicy: formState.s_ReturnPolicy || "",
        IsShowWarningOnDeletingCustomerData: Boolean(formState.isShowWarningOnDeletingCustomerData),
        ManageCoversOnTablePlan: Boolean(formState.manageCoversOnTablePlan),
        ...getVATPayloadFromForm(formState),
        IsTaxEnabled: Boolean(formState.isTaxEnabled),
        S_IsUpdatefromKitchen: Boolean(formState.s_IsUpdatefromKitchen),
        S_ShowCancelledOrderDuration: parseInt(formState.s_ShowCancelledOrderDuration) || 0,
        S_VisibleCancelOrderDaysinCancelTab: parseInt(formState.s_VisibleCancelOrderDaysinCancelTab) || 0,
        S_IsHaveLoyalty: Boolean(formState.s_IsHaveLoyalty),
        S_PointperPurchase: parseFloat(formState.s_PointperPurchase) || 0,
        S_PointSperDollar: parseFloat(formState.s_PointSperDollar) || 0,
        TV_ISOrderStatus: Boolean(formState.tV_ISOrderStatus),
        TV_ISOrderType: Boolean(formState.tV_ISOrderType),
        TV_ISOrderNumber: Boolean(formState.tV_ISOrderNumber),
        TV_ISOrderOwner: Boolean(formState.tV_ISOrderOwner),
        IsActivePreOrderModule: Boolean(formState.isActivePreOrderModule),
        PreOrderingCollection: Boolean(formState.preOrderingCollection),
        PreOrderingDelivery: Boolean(formState.preOrderingDelivery),
        PreOrderDayRange: parseInt(formState.preOrderDayRange) || 0,
        PreOrderAlertInMins: parseInt(formState.preOrderAlertInMins) || 20,
        CompDeliveryStatus: Boolean(formState.compDeliveryStatus),
        CompCollectionStatus: Boolean(formState.compCollectionStatus),
      };


      const response = await axiosInstance.post(
        APIs.POST_UPDATE_COMPANY_SETTING,
        updatedSettings,
        {
          headers: {
            'Content-Type': 'application/json',
          }
        }
      );

      if (response.data.status === true) {
        showSuccessToast("Table Reservation settings updated successfully");
        setIsTableReservationEditing(false);
        setTableReservationPreviousData(null);
      } else {
        const errorMessage = response.data?.Message || response.data?.message || "Failed to update Table Reservation settings";
        showErrorToast(errorMessage);
      }
    } catch (error) {
      console.error("Error in saveTableReservationSettings:", error);
      showErrorToast(
        error.response?.data?.Message ||
        error.response?.data?.message ||
        "Failed to update Table Reservation settings"
      );
    } finally {
      setLoading(false);
    }
  };

  const startKitchenEditing = () => {
    setIsKitchenEditing(true);
    setKitchenPreviousData({
      s_IsUpdatefromKitchen: formState.s_IsUpdatefromKitchen,
      s_ShowCancelledOrderDuration: formState.s_ShowCancelledOrderDuration,
      s_VisibleCancelOrderDaysinCancelTab: formState.s_VisibleCancelOrderDaysinCancelTab,
    });
  };

  const cancelKitchenEditing = () => {
    if (kitchenPreviousData) {
      setFormState((prev) => ({
        ...prev,
        ...kitchenPreviousData,
      }));
    }
    setIsKitchenEditing(false);
    setKitchenPreviousData(null);
  };

  const saveKitchenSettings = async () => {
    try {
      setLoading(true);

      const updatedSettings = {
        S_ID: formState.s_ID || 0,
        CompID: formState.CompID,
        // Update only Kitchen fields
        S_IsUpdatefromKitchen: Boolean(formState.s_IsUpdatefromKitchen),
        S_ShowCancelledOrderDuration: parseInt(formState.s_ShowCancelledOrderDuration) || 0,
        S_VisibleCancelOrderDaysinCancelTab: parseInt(formState.s_VisibleCancelOrderDaysinCancelTab) || 0,
        // Keep all other fields unchanged
        S_ServiceCharges: parseInt(formState.s_ServiceCharges) || 0,
        DeliveryCharges: formState.deliveryCharges || "0",
        S_MinOrderAmount: parseFloat(formState.s_MinOrderAmount) || 0,
        S_DeliveryTime: parseInt(formState.s_DeliveryTime) || 0,
        S_ReturnPolicy: formState.s_ReturnPolicy || "",
        IsShowWarningOnDeletingCustomerData: Boolean(formState.isShowWarningOnDeletingCustomerData),
        ManageCoversOnTablePlan: Boolean(formState.manageCoversOnTablePlan),
        ...getVATPayloadFromForm(formState),
        IsTaxEnabled: Boolean(formState.isTaxEnabled),
        S_TableReservationMarginTime: parseInt(formState.s_TableReservationMarginTime) || 0,
        S_TableReservedReleaseTime: parseInt(formState.s_TableReservedRelesaeTime) || 0,
        S_ShowingReserveredTableTime: parseInt(formState.s_ShowingReserveredTableTime) || 0,
        S_IsHaveLoyalty: Boolean(formState.s_IsHaveLoyalty),
        S_PointperPurchase: parseFloat(formState.s_PointperPurchase) || 0,
        S_PointSperDollar: parseFloat(formState.s_PointSperDollar) || 0,
        TV_ISOrderStatus: Boolean(formState.tV_ISOrderStatus),
        TV_ISOrderType: Boolean(formState.tV_ISOrderType),
        TV_ISOrderNumber: Boolean(formState.tV_ISOrderNumber),
        TV_ISOrderOwner: Boolean(formState.tV_ISOrderOwner),
        IsActivePreOrderModule: Boolean(formState.isActivePreOrderModule),
        PreOrderingCollection: Boolean(formState.preOrderingCollection),
        PreOrderingDelivery: Boolean(formState.preOrderingDelivery),
        PreOrderDayRange: parseInt(formState.preOrderDayRange) || 0,
        PreOrderAlertInMins: parseInt(formState.preOrderAlertInMins) || 20,
        CompDeliveryStatus: Boolean(formState.compDeliveryStatus),
        CompCollectionStatus: Boolean(formState.compCollectionStatus),
      };

      const response = await axiosInstance.post(
        APIs.POST_UPDATE_COMPANY_SETTING,
        updatedSettings,
        {
          headers: {
            'Content-Type': 'application/json',
          }
        }
      );

      if (response.data.status === true) {
        showSuccessToast("Kitchen settings updated successfully");
        setIsKitchenEditing(false);
        setKitchenPreviousData(null);
      } else {
        const errorMessage = response.data?.Message || response.data?.message || "Failed to update Kitchen settings";
        showErrorToast(errorMessage);
      }
    } catch (error) {
      console.error("Error in saveKitchenSettings:", error);
      showErrorToast(
        error.response?.data?.Message ||
        error.response?.data?.message ||
        "Failed to update Kitchen settings"
      );
    } finally {
      setLoading(false);
    }
  };

  const startLoyaltyEditing = () => {
    setIsLoyaltyEditing(true);
    setLoyaltyPreviousData({
      s_IsHaveLoyalty: formState.s_IsHaveLoyalty,
      s_PointperPurchase: formState.s_PointperPurchase,
      s_PointSperDollar: formState.s_PointSperDollar,
    });
  };

  const cancelLoyaltyEditing = () => {
    if (loyaltyPreviousData) {
      setFormState((prev) => ({
        ...prev,
        ...loyaltyPreviousData,
      }));
    }
    setIsLoyaltyEditing(false);
    setLoyaltyPreviousData(null);
  };

  const saveLoyaltySettings = async () => {
    try {
      setLoading(true);

      const updatedSettings = {
        S_ID: formState.s_ID || 0,
        CompID: formState.CompID,
        // Update only Loyalty fields
        S_IsHaveLoyalty: Boolean(formState.s_IsHaveLoyalty),
        S_PointperPurchase: parseFloat(formState.s_PointperPurchase) || 0,
        S_PointSperDollar: parseFloat(formState.s_PointSperDollar) || 0,
        // Keep all other fields unchanged
        S_ServiceCharges: parseInt(formState.s_ServiceCharges) || 0,
        DeliveryCharges: String(formState.deliveryCharges || "0"),
        S_MinOrderAmount: parseFloat(formState.s_MinOrderAmount) || 0,
        S_DeliveryTime: parseInt(formState.s_DeliveryTime) || 0,
        S_ReturnPolicy: formState.s_ReturnPolicy || "",
        IsShowWarningOnDeletingCustomerData: Boolean(formState.isShowWarningOnDeletingCustomerData),
        ManageCoversOnTablePlan: Boolean(formState.manageCoversOnTablePlan),
        ...getVATPayloadFromForm(formState),
        IsTaxEnabled: Boolean(formState.isTaxEnabled),
        S_TableReservationMarginTime: parseInt(formState.s_TableReservationMarginTime) || 0,
        S_TableReservedReleaseTime: parseInt(formState.s_TableReservedReleaseTime) || 0,
        S_ShowingReserveredTableTime: parseInt(formState.s_ShowingReserveredTableTime) || 0,
        S_IsUpdatefromKitchen: Boolean(formState.s_IsUpdatefromKitchen),
        S_ShowCancelledOrderDuration: parseInt(formState.s_ShowCancelledOrderDuration) || 0,
        S_VisibleCancelOrderDaysinCancelTab: parseInt(formState.s_VisibleCancelOrderDaysinCancelTab) || 0,
        TV_ISOrderStatus: Boolean(formState.tV_ISOrderStatus),
        TV_ISOrderType: Boolean(formState.tV_ISOrderType),
        TV_ISOrderNumber: Boolean(formState.tV_ISOrderNumber),
        TV_ISOrderOwner: Boolean(formState.tV_ISOrderOwner),
        IsActivePreOrderModule: Boolean(formState.isActivePreOrderModule),
        PreOrderingCollection: Boolean(formState.preOrderingCollection),
        PreOrderingDelivery: Boolean(formState.preOrderingDelivery),
        PreOrderDayRange: parseInt(formState.preOrderDayRange) || 0,
        PreOrderAlertInMins: parseInt(formState.preOrderAlertInMins) || 20,
        CompDeliveryStatus: Boolean(formState.compDeliveryStatus),
        CompCollectionStatus: Boolean(formState.compCollectionStatus),
      };



      const response = await axiosInstance.post(
        APIs.POST_UPDATE_COMPANY_SETTING,
        updatedSettings,
        {
          headers: {
            'Content-Type': 'application/json',
          }
        }
      );

      if (response.data && response.data.status === true) {
        showSuccessToast("Loyalty settings updated successfully");
        setIsLoyaltyEditing(false);
        setLoyaltyPreviousData(null);
      } else {
        const errorMessage = response.data?.Message || response.data?.message || "Failed to update Loyalty settings";
        showErrorToast(errorMessage);
      }
    } catch (error) {
      console.error("Error in saveLoyaltySettings:", error);
      showErrorToast(
        error.response?.data?.Message ||
        error.response?.data?.message ||
        "Failed to update Loyalty settings"
      );
    } finally {
      setLoading(false);
    }
  };

  const startTVAppEditing = () => {
    setIsTVAppEditing(true);
    setTVAppPreviousData({
      tV_ISOrderStatus: formState.tV_ISOrderStatus,
      tV_ISOrderType: formState.tV_ISOrderType,
      tV_ISOrderNumber: formState.tV_ISOrderNumber,
      tV_ISOrderOwner: formState.tV_ISOrderOwner,
    });
  };

  const cancelTVAppEditing = () => {
    if (tvAppPreviousData) {
      setFormState((prev) => ({
        ...prev,
        ...tvAppPreviousData,
      }));
    }
    setIsTVAppEditing(false);
    setTVAppPreviousData(null);
  };

  const saveTVAppSettings = async () => {
    try {
      setLoading(true);

      const updatedSettings = {
        S_ID: formState.s_ID || 0,
        CompID: formState.CompID,
        // Update only TV App fields
        TV_ISOrderStatus: Boolean(formState.tV_ISOrderStatus),
        TV_ISOrderType: Boolean(formState.tV_ISOrderType),
        TV_ISOrderNumber: Boolean(formState.tV_ISOrderNumber),
        TV_ISOrderOwner: Boolean(formState.tV_ISOrderOwner),
        // Keep all other fields unchanged
        S_ServiceCharges: parseInt(formState.s_ServiceCharges) || 0,
        DeliveryCharges: String(formState.deliveryCharges || "0"),
        S_MinOrderAmount: parseFloat(formState.s_MinOrderAmount) || 0,
        S_DeliveryTime: parseInt(formState.s_DeliveryTime) || 0,
        S_ReturnPolicy: formState.s_ReturnPolicy || "",
        IsShowWarningOnDeletingCustomerData: Boolean(formState.isShowWarningOnDeletingCustomerData),
        ManageCoversOnTablePlan: Boolean(formState.manageCoversOnTablePlan),
        ...getVATPayloadFromForm(formState),
        IsTaxEnabled: Boolean(formState.isTaxEnabled),
        S_TableReservationMarginTime: parseInt(formState.s_TableReservationMarginTime) || 0,
        S_TableReservedReleaseTime: parseInt(formState.s_TableReservedReleaseTime) || 0,
        S_ShowingReserveredTableTime: parseInt(formState.s_ShowingReserveredTableTime) || 0,
        S_IsUpdatefromKitchen: Boolean(formState.s_IsUpdatefromKitchen),
        S_ShowCancelledOrderDuration: parseInt(formState.s_ShowCancelledOrderDuration) || 0,
        S_VisibleCancelOrderDaysinCancelTab: parseInt(formState.s_VisibleCancelOrderDaysinCancelTab) || 0,
        S_IsHaveLoyalty: Boolean(formState.s_IsHaveLoyalty),
        S_PointperPurchase: parseFloat(formState.s_PointperPurchase) || 0,
        S_PointSperDollar: parseFloat(formState.s_PointSperDollar) || 0,
        IsActivePreOrderModule: Boolean(formState.isActivePreOrderModule),
        PreOrderingCollection: Boolean(formState.preOrderingCollection),
        PreOrderingDelivery: Boolean(formState.preOrderingDelivery),
        PreOrderDayRange: parseInt(formState.preOrderDayRange) || 0,
        PreOrderAlertInMins: parseInt(formState.preOrderAlertInMins) || 20,
        CompDeliveryStatus: Boolean(formState.compDeliveryStatus),
        CompCollectionStatus: Boolean(formState.compCollectionStatus),
      };



      const response = await axiosInstance.post(
        APIs.POST_UPDATE_COMPANY_SETTING,
        updatedSettings,
        {
          headers: {
            'Content-Type': 'application/json',
          }
        }
      );

      if (response.data && response.data.status === true) {
        showSuccessToast("TV App settings updated successfully");
        setIsTVAppEditing(false);
        setTVAppPreviousData(null);
      } else {
        const errorMessage = response.data?.Message || response.data?.message || "Failed to update TV App settings";
        showErrorToast(errorMessage);
      }
    } catch (error) {
      console.error("Error in saveTVAppSettings:", error);
      showErrorToast(
        error.response?.data?.Message ||
        error.response?.data?.message ||
        "Failed to update TV App settings"
      );
    } finally {
      setLoading(false);
    }
  };

  const saveGeneralSettings = async () => {
    try {
      setLoading(true);

      // Create complete settings data with only General fields updated
      const updatedSettings = {
        S_ID: formState.s_ID || 0,
        CompID: formState.CompID,
        S_ServiceCharges: parseInt(formState.s_ServiceCharges) || 0,
        DeliveryCharges: String(formState.deliveryCharges || "0"),
        S_MinOrderAmount: parseFloat(formState.s_MinOrderAmount) || 0,
        S_DeliveryTime: parseInt(formState.s_DeliveryTime) || 0,
        S_ReturnPolicy: formState.s_ReturnPolicy || "",
        IsShowWarningOnDeletingCustomerData: Boolean(formState.isShowWarningOnDeletingCustomerData),
        ManageCoversOnTablePlan: Boolean(formState.manageCoversOnTablePlan),
        // Keep all other fields unchanged
        ...getVATPayloadFromForm(formState),
        IsTaxEnabled: Boolean(formState.isTaxEnabled),
        S_TableReservationMarginTime: parseInt(formState.s_TableReservationMarginTime) || 0,
        S_TableReservedReleaseTime: parseInt(formState.s_TableReservedReleaseTime) || 0,
        S_ShowingReserveredTableTime: parseInt(formState.s_ShowingReserveredTableTime) || 0,
        S_IsUpdatefromKitchen: Boolean(formState.s_IsUpdatefromKitchen),
        S_ShowCancelledOrderDuration: parseInt(formState.s_ShowCancelledOrderDuration) || 0,
        S_VisibleCancelOrderDaysinCancelTab: parseInt(formState.s_VisibleCancelOrderDaysinCancelTab) || 0,
        S_IsHaveLoyalty: Boolean(formState.s_IsHaveLoyalty),
        S_PointperPurchase: parseFloat(formState.s_PointperPurchase) || 0,
        S_PointSperDollar: parseFloat(formState.s_PointSperDollar) || 0,
        TV_ISOrderStatus: Boolean(formState.tV_ISOrderStatus),
        TV_ISOrderType: Boolean(formState.tV_ISOrderType),
        TV_ISOrderNumber: Boolean(formState.tV_ISOrderNumber),
        TV_ISOrderOwner: Boolean(formState.tV_ISOrderOwner),
        IsActivePreOrderModule: Boolean(formState.isActivePreOrderModule),
        PreOrderingCollection: Boolean(formState.preOrderingCollection),
        PreOrderingDelivery: Boolean(formState.preOrderingDelivery),
        PreOrderDayRange: parseInt(formState.preOrderDayRange) || 0,
        PreOrderAlertInMins: parseInt(formState.preOrderAlertInMins) || 20,
        CompDeliveryStatus: Boolean(formState.compDeliveryStatus),
        CompCollectionStatus: Boolean(formState.compCollectionStatus),
      };



      const response = await axiosInstance.post(
        APIs.POST_UPDATE_COMPANY_SETTING,
        updatedSettings,
        {
          headers: {
            'Content-Type': 'application/json',
          }
        }
      );

      if (response.data.status === true) {
        showSuccessToast("General settings updated successfully");
        setIsGeneralEditing(false);
        setGeneralPreviousData(null);
      } else {
        const errorMessage = response.data?.Message || response.data?.message || "Failed to update General settings";
        showErrorToast(errorMessage);
      }
    } catch (error) {
      console.error("Error in saveGeneralSettings:", error);
      console.error("Error response:", error.response?.data);
      showErrorToast(
        error.response?.data?.Message ||
        error.response?.data?.message ||
        "Failed to update General settings"
      );
    } finally {
      setLoading(false);
    }
  };

  const saveOnlineOrderingSettings = async () => {
    try {
      setLoading(true);

      // Create complete settings data with only Online Ordering fields updated
      const updatedSettings = {
        S_ID: formState.s_ID || 0,
        CompID: formState.CompID,
        // Update only Online Ordering fields
        IsActivePreOrderModule: Boolean(formState.isActivePreOrderModule),
        PreOrderingCollection: Boolean(formState.preOrderingCollection),
        PreOrderingDelivery: Boolean(formState.preOrderingDelivery),
        PreOrderDayRange: parseInt(formState.preOrderDayRange) || 0,
        PreOrderAlertInMins: parseInt(formState.preOrderAlertInMins) || 20,
        CompDeliveryStatus: Boolean(formState.compDeliveryStatus),
        CompCollectionStatus: Boolean(formState.compCollectionStatus),
        // Keep all other fields unchanged
        S_ServiceCharges: parseInt(formState.s_ServiceCharges) || 0,
        DeliveryCharges: String(formState.deliveryCharges || "0"),
        S_MinOrderAmount: parseFloat(formState.s_MinOrderAmount) || 0,
        S_DeliveryTime: parseInt(formState.s_DeliveryTime) || 0,
        S_ReturnPolicy: formState.s_ReturnPolicy || "",
        IsShowWarningOnDeletingCustomerData: Boolean(formState.isShowWarningOnDeletingCustomerData),
        ManageCoversOnTablePlan: Boolean(formState.manageCoversOnTablePlan),
        ...getVATPayloadFromForm(formState),
        IsTaxEnabled: Boolean(formState.isTaxEnabled),
        S_TableReservationMarginTime: parseInt(formState.s_TableReservationMarginTime) || 0,
        S_TableReservedReleaseTime: parseInt(formState.s_TableReservedReleaseTime) || 0,
        S_ShowingReserveredTableTime: parseInt(formState.s_ShowingReserveredTableTime) || 0,
        S_IsUpdatefromKitchen: Boolean(formState.s_IsUpdatefromKitchen),
        S_ShowCancelledOrderDuration: parseInt(formState.s_ShowCancelledOrderDuration) || 0,
        S_VisibleCancelOrderDaysinCancelTab: parseInt(formState.s_VisibleCancelOrderDaysinCancelTab) || 0,
        S_IsHaveLoyalty: Boolean(formState.s_IsHaveLoyalty),
        S_PointperPurchase: parseFloat(formState.s_PointperPurchase) || 0,
        S_PointSperDollar: parseFloat(formState.s_PointSperDollar) || 0,
        TV_ISOrderStatus: Boolean(formState.tV_ISOrderStatus),
        TV_ISOrderType: Boolean(formState.tV_ISOrderType),
        TV_ISOrderNumber: Boolean(formState.tV_ISOrderNumber),
        TV_ISOrderOwner: Boolean(formState.tV_ISOrderOwner),
      };


      const response = await axiosInstance.post(
        APIs.POST_UPDATE_COMPANY_SETTING,
        updatedSettings,
        {
          headers: {
            'Content-Type': 'application/json',
          }
        }
      );

      if (response.data && response.data.status === true) {
        showSuccessToast("Online Ordering settings updated successfully");
        setIsOnlineOrderingEditing(false);
        setOnlineOrderingPreviousData(null);
      } else {
        const errorMessage = response.data?.Message || response.data?.message || "Failed to update Online Ordering settings";
        showErrorToast(errorMessage);
      }
    } catch (error) {
      console.error("Error in saveOnlineOrderingSettings:", error);
      console.error("Error response:", error.response?.data);
      showErrorToast(
        error.response?.data?.Message ||
        error.response?.data?.message ||
        "Failed to update Online Ordering settings"
      );
    } finally {
      setLoading(false);
    }
  };

  const saveField = async (name, value = editValue) => {

    try {
      setLoading(true);
      const sanitizedValue =
        typeof value === "object" && value !== null
          ? value.target?.checked ?? value
          : value;

      if (
        name === "deliveryCharges" &&
        (isNaN(sanitizedValue) || sanitizedValue === "")
      ) {
        showErrorToast("Delivery Charges must be a valid number");
        return;
      }

      // Handle Restaurant Hours only
      if (name === "weeklyServiceHours") {
        const timing = {
          aT_ID: timingData.weeklyServiceHours[0]?.aT_ID || 0,
          compID: CompID,
          localizeID: "en",
          type: "Restaurant",
        };

        const days = [
          "Monday",
          "Tuesday",
          "Wednesday",
          "Thursday",
          "Friday",
          "Saturday",
          "Sunday",
        ];

        if (enableSchedule.weeklyServiceHours) {
          days.forEach((day) => {
            const sObj = schedules.weeklyServiceHours.find((s) => s.day === day);
            if (sObj?.checked && sObj.timeSlots.length > 0) {
              timing[`aT_${day}Opening`] = moment(sObj.timeSlots[0].from, [
                "HH:mm",
                "h:mm A",
              ]).format("HH:mm");
              timing[`aT_${day}Closing`] = moment(sObj.timeSlots[0].to, [
                "HH:mm",
                "h:mm A",
              ]).format("HH:mm");
            } else {
              timing[`aT_${day}Opening`] = null;
              timing[`aT_${day}Closing`] = null;
            }
          });
        } else {
          days.forEach((day) => {
            timing[`aT_${day}Opening`] = null;
            timing[`aT_${day}Closing`] = null;
          });
        }

        const updatedTimingData = { weeklyServiceHours: [timing] };
        setTimingData(updatedTimingData);

        const updatedSettings = {
          S_ID: formState.s_ID || 0,
          CompID: formState.CompID,
          // Restaurant Hours only — Delivery/Collection schedules no longer edited here
          timings: updatedTimingData.weeklyServiceHours,
          // Keep all other fields unchanged
          S_ServiceCharges: parseInt(formState.s_ServiceCharges) || 0,
          DeliveryCharges: String(formState.deliveryCharges || "0"),
          S_MinOrderAmount: parseFloat(formState.s_MinOrderAmount) || 0,
          S_DeliveryTime: parseInt(formState.s_DeliveryTime) || 0,
          S_ReturnPolicy: formState.s_ReturnPolicy || "",
          IsShowWarningOnDeletingCustomerData: Boolean(formState.isShowWarningOnDeletingCustomerData),
          ManageCoversOnTablePlan: Boolean(formState.manageCoversOnTablePlan),
          ...getVATPayloadFromForm(formState),
          IsTaxEnabled: Boolean(formState.isTaxEnabled),
          S_TableReservationMarginTime: parseInt(formState.s_TableReservationMarginTime) || 0,
          S_TableReservedReleaseTime: parseInt(formState.s_TableReservedReleaseTime) || 0,
          S_ShowingReserveredTableTime: parseInt(formState.s_ShowingReserveredTableTime) || 0,
          S_IsUpdatefromKitchen: Boolean(formState.s_IsUpdatefromKitchen),
          S_ShowCancelledOrderDuration: parseInt(formState.s_ShowCancelledOrderDuration) || 0,
          S_VisibleCancelOrderDaysinCancelTab: parseInt(formState.s_VisibleCancelOrderDaysinCancelTab) || 0,
          S_IsHaveLoyalty: Boolean(formState.s_IsHaveLoyalty),
          S_PointperPurchase: parseFloat(formState.s_PointperPurchase) || 0,
          S_PointSperDollar: parseFloat(formState.s_PointSperDollar) || 0,
          TV_ISOrderStatus: Boolean(formState.tV_ISOrderStatus),
          TV_ISOrderType: Boolean(formState.tV_ISOrderType),
          TV_ISOrderNumber: Boolean(formState.tV_ISOrderNumber),
          TV_ISOrderOwner: Boolean(formState.tV_ISOrderOwner),
          IsActivePreOrderModule: Boolean(formState.isActivePreOrderModule),
          PreOrderingCollection: Boolean(formState.preOrderingCollection),
          PreOrderingDelivery: Boolean(formState.preOrderingDelivery),
          PreOrderDayRange: parseInt(formState.preOrderDayRange) || 0,
          PreOrderAlertInMins: parseInt(formState.preOrderAlertInMins) || 20,
          CompDeliveryStatus: Boolean(formState.compDeliveryStatus),
          CompCollectionStatus: Boolean(formState.compCollectionStatus),
        };
        console.log("Submitting timing update:", updatedSettings);


        const response = await axiosInstance.post(
          APIs.POST_UPDATE_COMPANY_SETTING,
          updatedSettings,
          {
            headers: {
              'Content-Type': 'application/json',
            }
          }
        );

        if (response.data.status) {
          showSuccessToast(`${name} updated successfully`);
        } else {
          showErrorToast(response.data.Message || "Failed to update");
        }
        cancelEditingAfterSubmit();
        return;
      }

      // Handle tax section save
      if (name === "Tax") {

        const updatedSettings = {
          S_ID: formState.s_ID || 0,
          CompID: formState.CompID,
          // Update only VAT fields
          IsTaxEnabled: Boolean(formState.isTaxEnabled),
          ...getVATPayloadFromForm(formState),
          // Keep all other fields unchanged
          S_ServiceCharges: parseInt(formState.s_ServiceCharges) || 0,
          DeliveryCharges: String(formState.deliveryCharges || "0"),
          S_MinOrderAmount: parseFloat(formState.s_MinOrderAmount) || 0,
          S_DeliveryTime: parseInt(formState.s_DeliveryTime) || 0,
          S_ReturnPolicy: formState.s_ReturnPolicy || "",
          IsShowWarningOnDeletingCustomerData: Boolean(formState.isShowWarningOnDeletingCustomerData),
          ManageCoversOnTablePlan: Boolean(formState.manageCoversOnTablePlan),
          S_TableReservationMarginTime: parseInt(formState.s_TableReservationMarginTime) || 0,
          S_TableReservedReleaseTime: parseInt(formState.s_TableReservedReleaseTime) || 0,
          S_ShowingReserveredTableTime: parseInt(formState.s_ShowingReserveredTableTime) || 0,
          S_IsUpdatefromKitchen: Boolean(formState.s_IsUpdatefromKitchen),
          S_ShowCancelledOrderDuration: parseInt(formState.s_ShowCancelledOrderDuration) || 0,
          S_VisibleCancelOrderDaysinCancelTab: parseInt(formState.s_VisibleCancelOrderDaysinCancelTab) || 0,
          S_IsHaveLoyalty: Boolean(formState.s_IsHaveLoyalty),
          S_PointperPurchase: parseFloat(formState.s_PointperPurchase) || 0,
          S_PointSperDollar: parseFloat(formState.s_PointSperDollar) || 0,
          TV_ISOrderStatus: Boolean(formState.tV_ISOrderStatus),
          TV_ISOrderType: Boolean(formState.tV_ISOrderType),
          TV_ISOrderNumber: Boolean(formState.tV_ISOrderNumber),
          TV_ISOrderOwner: Boolean(formState.tV_ISOrderOwner),
          IsActivePreOrderModule: Boolean(formState.isActivePreOrderModule),
          PreOrderingCollection: Boolean(formState.preOrderingCollection),
          PreOrderingDelivery: Boolean(formState.preOrderingDelivery),
          PreOrderDayRange: parseInt(formState.preOrderDayRange) || 0,
          PreOrderAlertInMins: parseInt(formState.preOrderAlertInMins) || 20,
          CompDeliveryStatus: Boolean(formState.compDeliveryStatus),
          CompCollectionStatus: Boolean(formState.compCollectionStatus),
        };
        console.log("Updating VAT settings with data:", updatedSettings);


        const response = await axiosInstance.post(
          APIs.POST_UPDATE_COMPANY_SETTING,
          updatedSettings,
          {
            headers: {
              'Content-Type': 'application/json',
            }
          }
        );


        if (response.data.status) {
          showSuccessToast("VAT settings updated successfully");
          setTaxEditing(false);
          setPreviousSettingsData(null);
        } else {
          showErrorToast(response.data.Message || "Failed to update VAT settings");
        }

        return;
      }

      setFormState((prev) => ({
        ...prev,
        [name]: sanitizedValue,
      }));

      // Create complete settings data with only the specific field updated
      const completeSettingsData = {
        S_ID: formState.s_ID || 0,
        CompID: formState.CompID,
        // Update only the specific field
        [name]: sanitizedValue,
        // Keep all other fields unchanged
        S_ServiceCharges: parseInt(formState.s_ServiceCharges) || 0,
        DeliveryCharges: String(formState.deliveryCharges || "0"),
        S_MinOrderAmount: parseFloat(formState.s_MinOrderAmount) || 0,
        S_DeliveryTime: parseInt(formState.s_DeliveryTime) || 0,
        S_ReturnPolicy: formState.s_ReturnPolicy || "",
        IsShowWarningOnDeletingCustomerData: Boolean(formState.isShowWarningOnDeletingCustomerData),
        ManageCoversOnTablePlan: Boolean(formState.manageCoversOnTablePlan),
        ...getVATPayloadFromForm(formState),
        IsTaxEnabled: Boolean(formState.isTaxEnabled),
        S_TableReservationMarginTime: parseInt(formState.s_TableReservationMarginTime) || 0,
        S_TableReservedReleaseTime: parseInt(formState.s_TableReservedReleaseTime) || 0,
        S_ShowingReserveredTableTime: parseInt(formState.s_ShowingReserveredTableTime) || 0,
        S_IsUpdatefromKitchen: Boolean(formState.s_IsUpdatefromKitchen),
        S_ShowCancelledOrderDuration: parseInt(formState.s_ShowCancelledOrderDuration) || 0,
        S_VisibleCancelOrderDaysinCancelTab: parseInt(formState.s_VisibleCancelOrderDaysinCancelTab) || 0,
        S_IsHaveLoyalty: Boolean(formState.s_IsHaveLoyalty),
        S_PointperPurchase: parseFloat(formState.s_PointperPurchase) || 0,
        S_PointSperDollar: parseFloat(formState.s_PointSperDollar) || 0,
        TV_ISOrderStatus: Boolean(formState.tV_ISOrderStatus),
        TV_ISOrderType: Boolean(formState.tV_ISOrderType),
        TV_ISOrderNumber: Boolean(formState.tV_ISOrderNumber),
        TV_ISOrderOwner: Boolean(formState.tV_ISOrderOwner),
        IsActivePreOrderModule: Boolean(formState.isActivePreOrderModule),
        PreOrderingCollection: Boolean(formState.preOrderingCollection),
        PreOrderingDelivery: Boolean(formState.preOrderingDelivery),
        PreOrderDayRange: parseInt(formState.preOrderDayRange) || 0,
        PreOrderAlertInMins: parseInt(formState.preOrderAlertInMins) || 20,
        CompDeliveryStatus: Boolean(formState.compDeliveryStatus),
        CompCollectionStatus: Boolean(formState.compCollectionStatus),
      };

      const response = await axiosInstance.post(
        APIs.POST_UPDATE_COMPANY_SETTING,
        completeSettingsData,
        {
          headers: {
            'Content-Type': 'application/json',
          }
        }
      );

      if (response.data.status) {
        showSuccessToast("Setting Data Updated successfully");
      } else {
        showErrorToast(response.data.Message || "Failed to update");
      }

      cancelEditingAfterSubmit();
    } catch (error) {
      showErrorToast(`Failed to update ${name}`);
    } finally {
      setLoading(false);
    }
  };

  const handlePaymentMethodSubmit = async (paymentData, isUpdate) => {
    try {
      setLoading(true);
      const url = isUpdate
        ? `${APIs.UPDATE_PAYMENT_METHOD}`
        : `${APIs.ADD_PAYMENT_METHOD}`;
      const method = isUpdate ? "patch" : "post";

      const response = await axiosInstance[method](url, paymentData);

      if (response.data.status) {
        showSuccessToast(
          response.data.Message ||
          `Payment method ${isUpdate ? "updated" : "added"} successfully`
        );

        if (isUpdate) {
          setPaymentMethods((prev) =>
            prev.map((pm) =>
              pm.id === paymentData.ID ? { ...pm, ...paymentData } : pm
            )
          );
        } else {
          setPaymentMethods((prev) => [...prev, paymentData]);
        }
      } else {
        showErrorToast(
          response.data.Message ||
          `Failed to ${isUpdate ? "update" : "add"} payment method`
        );
      }
    } catch (error) {
      showErrorToast(
        error.response?.data?.Message ||
        `Error ${isUpdate ? "updating" : "adding"} payment method`
      );
    } finally {
      setLoading(false);
    }
  };

  const handleSubscriptionSubmit = async (paymentMethod) => {
    try {
      setLoading(true);
      const response = await axiosInstance.post(APIs.SUBSCRIBE_PLUS_PLAN, {
        compID: CompID,
        subscriptionDate: new Date().toISOString(),
        paymentMethod: paymentMethod,
      });

      if (response.data.status) {
        showSuccessToast(
          response.data.Message || "Successfully subscribed to Plus plan"
        );
      } else {
        showErrorToast(
          response.data.Message || "Failed to subscribe to Plus plan"
        );
      }
    } catch (error) {
      showErrorToast(
        error.response?.data?.Message || "Error subscribing to Plus plan"
      );
    } finally {
      setLoading(false);
    }
  };

  const renderOnlineOrderingField = (label, name, value, type = "text", options = null) => {
    return (
      <div className="list-group-item d-flex align-items-center">
        <div className="flex-fill">
          <div>{label}</div>
          <div className="mt-2">
            {type === "checkbox" ? (
              <ToggleSwitch
                isChecked={value}
                onToggle={(checked) => {
                  setFormState((prev) => ({
                    ...prev,
                    [name]: checked,
                  }));
                }}
                label={label}
                name={name}
                themeColor={themeColor}
              />
            ) : type === "select" ? (
              <Select
                options={options}
                value={options.find((o) => o.value === value)}
                onChange={(option) => {
                  setFormState((prev) => ({
                    ...prev,
                    [name]: option ? option.value : "",
                  }));
                }}
                placeholder={`Select ${label}`}
                isClearable
              />
            ) : type === "textarea" ? (
              <textarea
                className="form-control"
                rows="4"
                value={value}
                onChange={(e) => {
                  setFormState((prev) => ({
                    ...prev,
                    [name]: e.target.value,
                  }));
                }}
                placeholder={`Enter ${label}`}
                name={name}
              />
            ) : (
              <FormInput
                type={type}
                value={value}
                onchange={(e) => {
                  // Special handling for deliveryCharges - keep as string
                  if (name === "deliveryCharges") {
                    const numValue = Number(e.target.value);
                    if (e.target.value !== "" && (isNaN(numValue) || numValue < 0)) {
                      showErrorToast("Delivery Charges must be a positive number");
                      return;
                    }
                    const stringValue = e.target.value === "" ? "" : String(e.target.value);
                    setFormState((prev) => ({
                      ...prev,
                      [name]: stringValue,
                    }));
                    return;
                  }

                  const sanitizedValue = [
                    "s_ServiceCharges",
                    "s_VAT",
                    "s_ServicesChargesOnCovers",
                    "s_MinOrderAmount",
                    "s_DeliveryTime",
                    "s_TableReservationMarginTime",
                    "s_TableReservedReleaseTime",
                    "s_ShowingReserveredTableTime",
                    "s_ShowCancelledOrderDuration",
                    "s_VisibleCancelOrderDaysinCancelTab",
                    "s_PointperPurchase",
                    "s_PointSperDollar",
                    "preOrderAlertInMins",
                  ].includes(name)
                    ? isNaN(Number(e.target.value)) || e.target.value === ""
                      ? ""
                      : Number(e.target.value)
                    : e.target.value;

                  if (typeof sanitizedValue === "number" && sanitizedValue < 0) {
                    showErrorToast(
                      `${name.replace(/([A-Z])/g, " $1").trim()} must be a positive number`
                    );
                    return;
                  }

                  setFormState((prev) => ({
                    ...prev,
                    [name]: sanitizedValue,
                  }));
                }}
                placeHolder={`Enter ${label}`}
                name={name}
                {...(type === "number" && name === "deliveryCharges"
                  ? { min: 0, step: "0.01" }
                  : {})}
              />
            )}
          </div>
          <div className="text-muted mt-1">
            {type === "checkbox"
              ? value
                ? "Enabled"
                : "Disabled"
              : value || "Not set"}
          </div>
        </div>
      </div>
    );
  };

  const renderServiceHoursDisplay = (name, value, label) => {
    const days = [
      "Monday",
      "Tuesday",
      "Wednesday",
      "Thursday",
      "Friday",
      "Saturday",
      "Sunday",
    ];

    const hasSchedule = value.length > 0 &&
      value[0] &&
      (value[0].aT_MondayOpening ||
        value[0].aT_TuesdayOpening ||
        value[0].aT_WednesdayOpening ||
        value[0].aT_ThursdayOpening ||
        value[0].aT_FridayOpening ||
        value[0].aT_SaturdayOpening ||
        value[0].aT_SundayOpening);

    if (!hasSchedule) {
      return (
        <div className="text-muted">
           <span className="badge" style={{ backgroundColor: '#6c757d' }}>
            <i className="fas fa-clock me-1"></i>
            Not configured
          </span>
        </div>
      );
    }

    const activeDays = days.filter(
      (day) =>
        value[0][`aT_${day}Opening`] &&
        value[0][`aT_${day}Closing`]
    );

    if (activeDays.length === 0) {
      return (
        <div className="text-muted">
          <span className="badge bg-info">
            <i className="fas fa-exclamation-triangle me-1"></i>
            No active days
          </span>
        </div>
      );
    }

    return (
      <div className="schedule-display">
        <div className="row g-2">
          {days.map((day) => {
            const isActive = value[0][`aT_${day}Opening`] && value[0][`aT_${day}Closing`];
            const openingTime = value[0][`aT_${day}Opening`];
            const closingTime = value[0][`aT_${day}Closing`];

            return (
              <div key={day} className="col-md-6 col-lg-4">
                <div className={`d-flex align-items-center p-2 rounded ${isActive ? 'bg-light-success' : 'bg-light-secondary'
                  }`}>
                  <div className="flex-fill">
                    <div className="fw-semibold text-sm">{day}</div>
                    <div className={`text-xs ${isActive ? 'text-success' : 'text-muted'
                      }`}>
                      {isActive ? (
                        <>
                          <i className="fas fa-clock me-1"></i>
                          {formatTimeForDisplay(openingTime)} - {formatTimeForDisplay(closingTime)}
                        </>
                      ) : (
                        <>
                          <i className="fas fa-times me-1"></i>
                          Closed
                        </>
                      )}
                    </div>
                  </div>
                  <div className="ms-2">
                    {isActive ? (
                      <span className="badge bg-info">
                        <i className="fas fa-check"></i>
                      </span>
                    ) : (
                      <span className="badge bg-info">
                        <i className="fas fa-times"></i>
                      </span>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <div className="mt-3">
          <div className="d-flex align-items-center text-muted">
            <i className="fas fa-info-circle me-2"></i>
            <span className="text-sm">
              {activeDays.length} of {days.length} days active
            </span>
          </div>
        </div>
      </div>
    );
  };

  const renderField = (label, name, value, type = "text", options = null) => {
    const isEditing = editingField === name;
    const days = [
      "Monday",
      "Tuesday",
      "Wednesday",
      "Thursday",
      "Friday",
      "Saturday",
      "Sunday",
    ];

    return (
      <div className="list-group-item d-flex align-items-center">
        <div className="flex-fill">
          <div>{label}</div>
          {isEditing ? (
            <div className="mt-2">
              {name === "weeklyServiceHours" ? (
                <div
                  className="card border-0 p-3"
                  style={{ backgroundColor: "#f5f5f5" }}
                >
                  <h5>{label}</h5>
                  <div className="mb-3">
                    <ToggleSwitch
                      isChecked={enableSchedule[name]}
                      onToggle={() =>
                        setEnableSchedule((prev) => ({
                          ...prev,
                          [name]: !prev[name],
                        }))
                      }
                      label={`Set ${label.toLowerCase()}`}
                      name={`enableSchedule-${name}`}
                      themeColor={themeColor}
                    />
                    <p className="text-muted mt-2">
                      Set the days of the week and times of day for{" "}
                      {label.toLowerCase()}.
                    </p>
                  </div>
                  {enableSchedule[name] && (
                    <div className="mb-4">
                      <div className="row">
                        <div className="col-12">
                          <ScheduleManager
                            schedules={schedules[name]}
                            setSchedules={(updatedSchedules) =>
                              setSchedules((prev) => ({
                                ...prev,
                                [name]: updatedSchedules,
                              }))
                            }
                            isProfilePage={true}
                          />
                        </div>
                      </div>
                    </div>
                  )}
                  <div className="mt-3">
                    <button
                      className="btn btn-rounded px-4 rounded-pill me-2"
                      style={{
                        backgroundColor: themeColor,
                        color: 'white',
                        border: 'none',
                        transition: 'all 0.3s ease'
                      }}
                      onClick={() => saveField(name)}
                      disabled={loading}
                    >
                      Save
                    </button>
                    <button
                      className="btn btn-secondary btn-rounded px-4 rounded-pill"
                      onClick={cancelEditing}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : type === "checkbox" ? (
                <ToggleSwitch
                  isChecked={value}
                  onToggle={(checked) => handleToggleChange(name, checked)}
                  label={label}
                  name={name}
                  themeColor={themeColor}
                />
              ) : type === "select" ? (
                <Select
                  options={options}
                  value={options.find((o) => o.value === editValue)}
                  onChange={(option) => handleSelectChange(name, option)}
                  placeholder={`Select ${label}`}
                  isClearable
                />
              ) : type === "textarea" ? (
                <textarea
                  className="form-control"
                  rows="4"
                  value={editValue}
                  onChange={(e) => handleInputChange(e)}
                  placeholder={`Enter ${label}`}
                  name={name}
                />
              ) : (
                <FormInput
                  type={type}
                  value={editValue}
                  onchange={(e) => handleInputChange(e)}
                  placeHolder={`Enter ${label}`}
                  name={name}
                  {...(type === "number" && name === "deliveryCharges"
                    ? { min: 0, step: "0.01" }
                    : {})}
                />
              )}
              {type !== "checkbox" &&
                name !== "weeklyServiceHours" && (
                  <div className="mt-2">
                    <button
                      className="btn btn-success btn-sm me-2"
                      onClick={() => saveField(name)}
                      disabled={loading}
                    >
                      Save
                    </button>
                    <button
                      className="btn btn-secondary btn-sm"
                      onClick={cancelEditing}
                      disabled={loading}
                    >
                      Cancel
                    </button>
                  </div>
                )}
            </div>
          ) : (
            <div className="text-muted">
              {name === "weeklyServiceHours"
                ? value.length > 0 &&
                  value[0] &&
                  (value[0].aT_MondayOpening ||
                    value[0].aT_TuesdayOpening ||
                    value[0].aT_WednesdayOpening ||
                    value[0].aT_ThursdayOpening ||
                    value[0].aT_FridayOpening ||
                    value[0].aT_SaturdayOpening ||
                    value[0].aT_SundayOpening)
                  ? days
                    .filter(
                      (day) =>
                        value[0][`aT_${day}Opening`] &&
                        value[0][`aT_${day}Closing`]
                    )
                    .map(
                      (day) =>
                        `${day}: ${formatTimeForDisplay(
                          value[0][`aT_${day}Opening`]
                        )} - ${formatTimeForDisplay(
                          value[0][`aT_${day}Closing`]
                        )}`
                    )
                    .join(", ")
                  : "Not set"
                : type === "checkbox"
                  ? value
                    ? "Enabled"
                    : "Disabled"
                  : value || "Not set"}
            </div>
          )}
        </div>
        {!isEditing && (
          <div>
            <button
              className="btn btn-secondary w-100px"
              onClick={() => startEditing(name, value)}
            >
              Edit
            </button>
          </div>
        )}
      </div>
    );
  };

  const renderTaxSection = () => {
    return (
      <div id="Tax" className="mb-4 pb-3">
        <h4 className="d-flex align-items-center mb-2">
          <i className="fas fa-receipt me-2" style={{ color: themeColor }}></i>
          VAT Management
        </h4>
        <p>Configure VAT settings.</p>

        {/* VAT Configuration Card - View Mode */}
        {!taxEditing && (
          <div className="card mb-3">
            <div className="card-body">
              <div className="d-flex align-items-center mb-3">
                <div className="flex-fill">
                  <div className="d-flex align-items-center mb-2">
                    <i className="fas fa-percentage me-2" style={{ color: themeColor }}></i>
                    <h5 className="mb-0 fw-semibold">% VAT On Order Level</h5>
                  </div>
                  {renderTaxStatusDisplay()}
                </div>
              </div>
              <div className="d-flex justify-content-end">
                <button
                  className="btn btn-secondary w-100px"
                  onClick={() => startEditing("Tax", null)}
                >
                  Edit
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Edit Mode */}
        {taxEditing && (
          <div className="card"
            style={{ borderColor: themeColor, borderWidth: '2px' }}>
            <div className="card-body">
              <div className="d-flex align-items-center mb-3">
                <i className="fas fa-percentage me-2" style={{ color: themeColor }}></i>
                <h5 className="mb-0 fw-semibold">% VAT On Order Level</h5>
              </div>

              {/* VAT Type - always visible whether VAT is enabled or disabled */}
              <div className="card mb-4">
                <div className="card-body">
                  <div className="d-flex align-items-center mb-2">
                    <i className="fas fa-tags me-2" style={{ color: themeColor }}></i>
                    <h6 className="mb-0 fw-semibold">VAT Type</h6>
                  </div>
                  <div className="form-check mb-2">
                    <input
                      className="form-check-input"
                      type="radio"
                      name="s_VARType"
                      id="s_VARType-inclusive"
                      value="inclusive"
                      checked={formState.s_VARType === "inclusive"}
                      onChange={(e) => handleInputChange(e)}
                    />
                    <label className="form-check-label" htmlFor="s_VARType-inclusive">
                      Inclusive
                    </label>
                  </div>
                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="radio"
                      name="s_VARType"
                      id="s_VARType-exclusive"
                      value="exclusive"
                      checked={formState.s_VARType === "exclusive"}
                      onChange={(e) => handleInputChange(e)}
                    />
                    <label className="form-check-label" htmlFor="s_VARType-exclusive">
                      Exclusive
                    </label>
                  </div>
                  <small className="text-muted">How VAT is calculated and displayed</small>
                </div>
              </div>

              <div className="mb-4">
                <ToggleSwitch
                  isChecked={formState.isTaxEnabled}
                  onToggle={(checked) => {
                    // When disabling tax, reset only rate and registration; keep VAT Type so it is always saved
                    if (!checked) {
                      setFormState((prev) => ({
                        ...prev,
                        isTaxEnabled: false,
                        s_VAT: "",
                        s_VATRegNo: "",
                      }));
                    } else {
                      setFormState((prev) => ({
                        ...prev,
                        isTaxEnabled: true,
                      }));
                    }
                  }}
                  label="Enable VAT On Order Level"
                  name="isTaxEnabled"
                  themeColor={themeColor}
                />
                <small className="text-muted mt-2 d-block">
                  (If enabled, VAT will be applied to Whole Order Amount){" "}
                  <b>AND</b> (if disabled VAT will be applied to each item individually)
                </small>
              </div>

              {/* VAT Rate and Registration - only show when tax is enabled */}
              {formState.isTaxEnabled && (
                <div className="row g-3 mb-4">
                  {/* VAT Percentage */}
                  <div className="col-md-6">
                    <div className="card h-100">
                      <div className="card-body">
                        <div className="d-flex align-items-center mb-2">
                          <i className="fas fa-percentage me-2" style={{ color: themeColor }}></i>
                          <h6 className="mb-0 fw-semibold">VAT Rate</h6>
                        </div>
                        <FormInput
                          type="number"
                          value={formState.s_VAT}
                          onchange={(e) => {
                            const sanitizedValue = isNaN(Number(e.target.value)) || e.target.value === ""
                              ? ""
                              : Number(e.target.value);

                            if (typeof sanitizedValue === "number" && sanitizedValue < 0) {
                              showErrorToast("VAT must be a positive number");
                              return;
                            }

                            setFormState((prev) => ({
                              ...prev,
                              s_VAT: sanitizedValue,
                            }));
                          }}
                          placeHolder="Enter VAT percentage"
                          name="s_VAT"
                          min="0"
                          step="0.01"
                        />
                        <small className="text-muted">Percentage rate for VAT calculation</small>
                      </div>
                    </div>
                  </div>

                  {/* VAT Registration Number */}
                  <div className="col-md-6">
                    <div className="card h-100">
                      <div className="card-body">
                        <div className="d-flex align-items-center mb-2">
                          <i className="fas fa-id-card me-2" style={{ color: themeColor }}></i>
                          <h6 className="mb-0 fw-semibold">VAT Registration</h6>
                        </div>
                        <FormInput
                          type="text"
                          value={formState.s_VATRegNo}
                          onchange={(e) => handleInputChange(e)}
                          placeHolder="Enter VAT registration number"
                          name="s_VATRegNo"
                        />
                        <small className="text-muted">Official VAT Registration Number</small>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <div className="d-flex justify-content-end gap-2 mt-3">
                <button
                  className="btn btn-outline-secondary btn-sm px-4"
                  onClick={cancelEditing}
                  disabled={loading}
                >
                  <i className="fas fa-times me-1"></i>
                  Cancel
                </button>
                <button
                  className="btn btn-sm px-4"
                  style={{
                    backgroundColor: themeColor,
                    color: 'white',
                    border: 'none',
                    transition: 'all 0.3s ease'
                  }}
                  onClick={() => {
                    console.log("VAT Save button clicked");
                    saveField("Tax");
                  }}
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                      Saving...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-save me-1"></i>
                      Save Changes
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderTaxStatusDisplay = () => {
    if (!formState.isTaxEnabled) {
      const vatTypeLabel = formState.s_VARType === "exclusive" ? "Exclusive" : "Inclusive";
      return (
        <div className="text-muted">
          <span className="badge fs-6" style={{ backgroundColor: '#6c757d' }}>
            <i className="fas fa-times me-1"></i>
            Disabled
          </span>
          <div className="mt-2">
            <small className="text-muted">
              <i className="fas fa-info-circle me-1"></i>
              VAT is currently disabled for this business
            </small>
          </div>
          <div className="mt-2 d-flex align-items-center p-2 rounded" style={{ backgroundColor: 'var(--bs-secondary-bg)', maxWidth: 'fit-content' }}>
            <i className="fas fa-tags me-2" style={{ color: themeColor }}></i>
            <div>
              <span className="fw-semibold text-sm">VAT Type: </span>
              <span className="text-sm">{vatTypeLabel}</span>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="tax-status-display">
        <div className="d-flex align-items-center mb-2">
          <span className="badge fs-6 me-3" style={{ backgroundColor: '#6c757d' }}>
            <i className="fas fa-check me-1"></i>
            Enabled
          </span>
        </div>

        <div className="row g-2">
          <div className="col-md-4">
            <div className="d-flex align-items-center p-2 bg-light-success rounded">
              <i className="fas fa-percentage me-2"></i>
              <div>
                <div className="fw-semibold text-sm">VAT Rate</div>
                <div className="text-success text-xs">
                  {formState.s_VAT || 0}%
                </div>
              </div>
            </div>
          </div>

          <div className="col-md-4">
            <div className="d-flex align-items-center p-2 bg-light-warning rounded">
              <i className="fas fa-id-card me-2"></i>
              <div>
                <div className="fw-semibold text-sm">VAT Registration</div>
                <div className="text-success text-xs">
                  {formState.s_VATRegNo || "Not set"}
                </div>
              </div>
            </div>
          </div>

          <div className="col-md-4">
            <div className="d-flex align-items-center p-2 bg-light-primary rounded">
              <i className="fas fa-tags me-2"></i>
              <div>
                <div className="fw-semibold text-sm">Type</div>
                <div className="text-primary text-xs">
                  {formState.s_VARType === "inclusive" ? "Inclusive" : "Exclusive"}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-3">
          <div className="d-flex align-items-center text-muted">
            <i className="fas fa-info-circle me-2"></i>
            <span className="text-sm">
              VAT is applied {formState.s_VARType === "inclusive" ? "inclusively" : "exclusively"} at {formState.s_VAT || 0}%
            </span>
          </div>
        </div>
      </div>
    );
  }


  return (
    <>
      <style>
        {`
          .bg-light-success {
            background-color: rgba(25, 135, 84, 0.1) !important;
            border: 1px solid rgba(25, 135, 84, 0.2);
          }
          .bg-light-secondary {
            background-color: rgba(108, 117, 125, 0.1) !important;
            border: 1px solid rgba(108, 117, 125, 0.2);
          }
          .text-xs {
            font-size: 0.75rem;
          }
          .text-sm {
            font-size: 0.85rem;
          }
          .fw-semibold {
            font-size: 0.95rem;
          }
          .card-body {
            font-size: 0.95rem;
          }
          .list-group-item {
            font-size: 0.95rem;
          }
          .navbar .nav-link {
            font-size: 0.95rem;
          }
          .schedule-display .row {
            margin-left: -0.5rem;
            margin-right: -0.5rem;
          }
          .schedule-display .col-md-6,
          .schedule-display .col-lg-4 {
            padding-left: 0.5rem;
            padding-right: 0.5rem;
          }
          .schedule-display .rounded {
            border-radius: 0.5rem !important;
          }
          .card.border-primary {
            border-color: #0d6efd !important;
            box-shadow: 0 0 0 0.2rem rgba(13, 110, 253, 0.25);
          }
          .bg-light-warning {
            background-color: rgba(255, 193, 7, 0.1) !important;
            border: 1px solid rgba(255, 193, 7, 0.2);
          }
          .bg-light-primary {
            background-color: rgba(13, 110, 253, 0.1) !important;
            border: 1px solid rgba(13, 110, 253, 0.2);
          }
          .tax-status-display .row {
            margin-left: -0.5rem;
            margin-right: -0.5rem;
          }
          .tax-status-display .col-md-4 {
            padding-left: 0.5rem;
            padding-right: 0.5rem;
          }
        `}
      </style>
      <div className="container-fluid px-4 py-4" style={{ fontSize: '13px' }}>
        {loading && <Preloader />}
        <div className="d-flex align-items-center mb-3">
          <div>
            <ol className="breadcrumb">
              <li className="breadcrumb-item">
                <a href="#/">Home</a>
              </li>
              <li className="breadcrumb-item active">Settings</li>
            </ol>
            <h1 className="page-header mb-0">
              <i className="fa fa-cog me-2" style={{ color: themeColor }}></i>
              Settings
            </h1>
          </div>
        </div>

        <div className="row">
          <div style={{ width: "230px" }}>
            <nav className="navbar navbar-sticky d-none d-xl-block my-n4 py-4 h-100 text-end">
              <nav className="nav" id="bsSpyTarget">
                <a
                  className="nav-link active"
                  href="#general"
                  data-toggle="scroll-to"
                  style={{
                    color: themeColor,
                    borderLeft: `3px solid ${themeColor}`,
                    paddingLeft: '12px'
                  }}
                >
                  <i className="fa fa-cog me-2"></i>General
                </a>
                <a
                  className="nav-link"
                  href="#Tax"
                  data-toggle="scroll-to"
                  style={{
                    color: isDarkMode ? '#d1d5db' : '#6b7280',
                    borderLeft: '3px solid transparent',
                    paddingLeft: '12px',
                    transition: 'all 0.3s ease'
                  }}
                >
                  <i className="fa fa-percentage me-2"></i>VAT Management
                </a>
                {/* Table Reservation - hidden for now
                <a
                  className="nav-link"
                  href="#TableReservation"
                  data-toggle="scroll-to"
                  style={{
                    color: isDarkMode ? '#d1d5db' : '#6b7280',
                    borderLeft: '3px solid transparent',
                    paddingLeft: '12px',
                    transition: 'all 0.3s ease'
                  }}
                >
                  <i className="fa fa-table me-2"></i>Table Reservation
                </a>
                */}
                {/* Loyalty Option - hidden for now
                <a
                  className="nav-link"
                  href="#LoyaltyOption"
                  data-toggle="scroll-to"
                  style={{
                    color: isDarkMode ? '#d1d5db' : '#6b7280',
                    borderLeft: '3px solid transparent',
                    paddingLeft: '12px',
                    transition: 'all 0.3s ease'
                  }}
                >
                  <i className="fa fa-gift me-2"></i>Loyalty Option
                </a>
                */}
                {/* Orders Tracking TV - hidden for now
                <a
                  className="nav-link"
                  href="#ChooseModeforOrdersTrackingTVApp"
                  data-toggle="scroll-to"
                  style={{
                    color: isDarkMode ? '#d1d5db' : '#6b7280',
                    borderLeft: '3px solid transparent',
                    paddingLeft: '12px',
                    transition: 'all 0.3s ease'
                  }}
                >
                  <i className="fa fa-tv me-2"></i>Orders Tracking TV
                </a>
                */}
                <a
                  className="nav-link"
                  href="#ServiceHours"
                  data-toggle="scroll-to"
                  style={{
                    color: isDarkMode ? '#d1d5db' : '#6b7280',
                    borderLeft: '3px solid transparent',
                    paddingLeft: '12px',
                    transition: 'all 0.3s ease'
                  }}
                >
                  <i className="fa fa-clock me-2"></i>Service Hours
                </a>
                <a
                  className="nav-link"
                  href="#PreOrderingModule"
                  data-toggle="scroll-to"
                  style={{
                    color: isDarkMode ? '#d1d5db' : '#6b7280',
                    borderLeft: '3px solid transparent',
                    paddingLeft: '12px',
                    transition: 'all 0.3s ease'
                  }}
                >
                  <i className="fa fa-shopping-cart me-2"></i>Online Ordering
                </a>
              </nav>
            </nav>
          </div>
          <div className="col-xl-8" id="bsSpyContent">
            <div id="general" className="mb-4 pb-3">
              <h4 className="d-flex align-items-center mb-2">
                <i className="fas fa-cog me-2" style={{ color: themeColor }}></i>
                General
              </h4>
              <p>Manage general settings for your business.</p>
              <div className="card">
                <div className="list-group list-group-flush fw-bold">
                  {!isGeneralEditing ? (
                    <>
                      {/* First Row - Service Charges and Delivery Charges */}
                      <div className="list-group-item">
                        <div className="row g-3">
                          <div className="col-md-6">
                            <div className="d-flex align-items-center mb-1">
                              <i className="fas fa-percentage me-2" style={{ color: themeColor }}></i>
                              <span className="fw-semibold">Service Charges (%)</span>
                            </div>
                            <div className="text-muted">
                              <span className="badge" style={{ backgroundColor: '#6c757d' }}>
                                {formState.s_ServiceCharges ? `${formState.s_ServiceCharges}%` : "Not set"}
                              </span>
                            </div>
                          </div>
                          <div className="col-md-6">
                            <div className="d-flex align-items-center mb-1">
                              <i className="fas fa-truck me-2" style={{ color: themeColor }}></i>
                              <span className="fw-semibold">Delivery Charges</span>
                            </div>
                            <div className="text-muted">
                              <span className="badge" style={{ backgroundColor: '#6c757d' }}>
                                {formState.deliveryCharges ? `$${formState.deliveryCharges}` : "Not set"}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Second Row - Minimum Order Amount and Delivery Time */}
                      <div className="list-group-item">
                        <div className="row g-3">
                          <div className="col-md-6">
                            <div className="d-flex align-items-center mb-1">
                              <i className="fas fa-dollar-sign me-2" style={{ color: themeColor }}></i>
                              <span className="fw-semibold">Minimum Order Amount</span>
                            </div>
                            <div className="text-muted">
                              <span className="badge" style={{ backgroundColor: '#6c757d' }}>
                                {formState.s_MinOrderAmount ? `$${formState.s_MinOrderAmount}` : "Not set"}
                              </span>
                            </div>
                          </div>
                          <div className="col-md-6">
                            <div className="d-flex align-items-center mb-1">
                              <i className="fas fa-clock me-2" style={{ color: themeColor }}></i>
                              <span className="fw-semibold">Delivery Time (min)</span>
                            </div>
                            <div className="text-muted">
                              <span className="badge" style={{ backgroundColor: '#6c757d' }}>
                                {formState.s_DeliveryTime ? `${formState.s_DeliveryTime} minutes` : "Not set"}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Return Policy - View Mode */}
                      <div className="list-group-item d-flex align-items-center">
                        <div className="flex-fill">
                          <div className="d-flex align-items-center mb-1">
                            <i className="fas fa-undo me-2" style={{ color: themeColor }}></i>
                            <span className="fw-semibold">Return Policy</span>
                          </div>
                          <div className="text-muted">
                            <span className="badge" style={{ backgroundColor: '#6c757d' }}>
                              {formState.s_ReturnPolicy ? "Set" : "Not set"}
                            </span>
                            {formState.s_ReturnPolicy && (
                              <div className="mt-2">
                                <small className="text-muted">
                                  {formState.s_ReturnPolicy.length > 100
                                    ? `${formState.s_ReturnPolicy.substring(0, 100)}...`
                                    : formState.s_ReturnPolicy}
                                </small>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>


                      {/* <div className="list-group-item bg-light">
                        <div className="d-flex align-items-center mb-3">
                          <i className="fas fa-exclamation-triangle me-2" style={{ color: themeColor }}></i>
                          <span className="fw-bold text-dark">Warning Settings</span>
                        </div>
                        <div className="row">
                          <div className="col-md-6">
                            <div className="d-flex align-items-center">
                              <div className="flex-fill">
                                <div className="d-flex align-items-center mb-1">
                                  <i className="fas fa-user-times me-2" style={{ color: themeColor }}></i>
                                  <span className="fw-semibold">Show Warning On Deleting Customer Data</span>
                                </div>
                                <div className="text-muted">
                                  <span className="badge bg-info">
                                    {formState.isShowWarningOnDeletingCustomerData ? "Enabled" : "Disabled"}
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>
                          <div className="col-md-6">
                            <div className="d-flex align-items-center">
                              <div className="flex-fill">
                                <div className="d-flex align-items-center mb-1">
                                  <i className="fas fa-chair me-2" style={{ color: themeColor }}></i>
                                  <span className="fw-semibold">Manage Covers of Table Plan</span>
                                </div>
                                <div className="text-muted">
                                  <span className="badge bg-info">
                                    {formState.manageCoversOnTablePlan ? "Enabled" : "Disabled"}
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div> */}

                      {/* Single Edit Button */}
                      <div className="list-group-item d-flex justify-content-end">
                        <button
                          className="btn btn-secondary w-100px"
                          onClick={startGeneralEditing}
                        >
                          Edit
                        </button>
                      </div>
                    </>
                  ) : (
                    <>
                      {/* First Row - Service Charges and Delivery Charges */}
                      <div className="list-group-item">
                        <div className="row g-3">
                          <div className="col-md-6">
                            <div className="d-flex align-items-center mb-2">
                              <i className="fas fa-percentage me-2" style={{ color: themeColor }}></i>
                              <span className="fw-semibold">Service Charges (%)</span>
                            </div>
                            <FormInput
                              type="number"
                              value={formState.s_ServiceCharges}
                              onchange={(e) => {
                                const sanitizedValue = isNaN(Number(e.target.value)) || e.target.value === ""
                                  ? ""
                                  : Number(e.target.value);

                                if (typeof sanitizedValue === "number" && sanitizedValue < 0) {
                                  showErrorToast("Service Charges must be a positive number");
                                  return;
                                }

                                setFormState((prev) => ({
                                  ...prev,
                                  s_ServiceCharges: sanitizedValue,
                                }));
                              }}
                              placeHolder="Enter service charges percentage"
                              name="s_ServiceCharges"
                              min="0"
                              step="0.01"
                            />
                          </div>
                          <div className="col-md-6">
                            <div className="d-flex align-items-center mb-2">
                              <i className="fas fa-truck me-2" style={{ color: themeColor }}></i>
                              <span className="fw-semibold">Delivery Charges</span>
                            </div>
                            <FormInput
                              type="number"
                              value={formState.deliveryCharges}
                              onchange={(e) => {
                                const sanitizedValue = isNaN(Number(e.target.value)) || e.target.value === ""
                                  ? ""
                                  : Number(e.target.value);

                                if (typeof sanitizedValue === "number" && sanitizedValue < 0) {
                                  showErrorToast("Delivery Charges must be a positive number");
                                  return;
                                }

                                setFormState((prev) => ({
                                  ...prev,
                                  deliveryCharges: sanitizedValue,
                                }));
                              }}
                              placeHolder="Enter delivery charges"
                              name="deliveryCharges"
                              min="0"
                              step="0.01"
                            />
                          </div>
                        </div>
                      </div>

                      {/* Second Row - Minimum Order Amount and Delivery Time */}
                      <div className="list-group-item">
                        <div className="row g-3">
                          <div className="col-md-6">
                            <div className="d-flex align-items-center mb-2">
                              <i className="fas fa-dollar-sign me-2" style={{ color: themeColor }}></i>
                              <span className="fw-semibold">Minimum Order Amount</span>
                            </div>
                            <FormInput
                              type="number"
                              value={formState.s_MinOrderAmount}
                              onchange={(e) => {
                                const sanitizedValue = isNaN(Number(e.target.value)) || e.target.value === ""
                                  ? ""
                                  : Number(e.target.value);

                                if (typeof sanitizedValue === "number" && sanitizedValue < 0) {
                                  showErrorToast("Minimum Order Amount must be a positive number");
                                  return;
                                }

                                setFormState((prev) => ({
                                  ...prev,
                                  s_MinOrderAmount: sanitizedValue,
                                }));
                              }}
                              placeHolder="Enter minimum order amount"
                              name="s_MinOrderAmount"
                              min="0"
                              step="0.01"
                            />
                          </div>
                          <div className="col-md-6">
                            <div className="d-flex align-items-center mb-2">
                              <i className="fas fa-clock me-2 " style={{ color: themeColor }}></i>
                              <span className="fw-semibold">Delivery Time (min)</span>
                            </div>
                            <FormInput
                              type="number"
                              value={formState.s_DeliveryTime}
                              onchange={(e) => {
                                const sanitizedValue = isNaN(Number(e.target.value)) || e.target.value === ""
                                  ? ""
                                  : Number(e.target.value);

                                if (typeof sanitizedValue === "number" && sanitizedValue < 0) {
                                  showErrorToast("Delivery Time must be a positive number");
                                  return;
                                }

                                setFormState((prev) => ({
                                  ...prev,
                                  s_DeliveryTime: sanitizedValue,
                                }));
                              }}
                              placeHolder="Enter delivery time in minutes"
                              name="s_DeliveryTime"
                              min="0"
                            />
                          </div>
                        </div>
                      </div>

                      {/* Return Policy - Edit Mode */}
                      <div className="list-group-item">
                        <div className="d-flex align-items-center mb-2">
                          <i className="fas fa-undo me-2" style={{ color: themeColor }}></i>
                          <span className="fw-semibold">Return Policy</span>
                        </div>
                        <div className="mt-2">
                          <textarea
                            className="form-control"
                            rows="4"
                            value={formState.s_ReturnPolicy}
                            onChange={(e) => {
                              setFormState((prev) => ({
                                ...prev,
                                s_ReturnPolicy: e.target.value,
                              }));
                            }}
                            placeholder="Enter return policy"
                            name="s_ReturnPolicy"
                          />
                        </div>
                      </div>

                      {/* Warning Settings - Edit Mode */}
                      {/* <div className="list-group-item bg-light">
                        <div className="d-flex align-items-center mb-3">
                          <i className="fas fa-exclamation-triangle me-2" style={{ color: themeColor }}></i>
                          <span className="fw-bold text-dark">Warning Settings</span>
                        </div>
                        <div className="row">
                          <div className="col-md-6">
                            <div className="d-flex align-items-center">
                              <div className="flex-fill">
                                <div className="d-flex align-items-center mb-2">
                                  <i className="fas fa-user-times me-2" style={{ color: themeColor }}></i>
                                  <span className="fw-semibold">Show Warning On Deleting Customer Data</span>
                                </div>
                                <div className="mt-2">
                                  <ToggleSwitch
                                    isChecked={formState.isShowWarningOnDeletingCustomerData}
                                    onToggle={(e) => {
                                      setFormState((prev) => ({
                                        ...prev,
                                        isShowWarningOnDeletingCustomerData: e.target?.checked ?? e,
                                      }));
                                    }}
                                    label="Show Warning On Deleting Customer Data"
                                    name="isShowWarningOnDeletingCustomerData"
                                    themeColor={themeColor}
                                  />
                                </div>
                              </div>
                            </div>
                          </div>
                          <div className="col-md-6">
                            <div className="d-flex align-items-center">
                              <div className="flex-fill">
                                <div className="d-flex align-items-center mb-2">
                                  <i className="fas fa-chair text-info me-2"></i>
                                  <span className="fw-semibold">Manage Covers of Table Plan</span>
                                </div>
                                <div className="mt-2">
                                  <ToggleSwitch
                                    isChecked={formState.manageCoversOnTablePlan}
                                    onToggle={(e) => {
                                      setFormState((prev) => ({
                                        ...prev,
                                        manageCoversOnTablePlan: e.target?.checked ?? e,
                                      }));
                                    }}
                                    label="Manage Covers of Table Plan"
                                    name="manageCoversOnTablePlan"
                                    themeColor={themeColor}
                                  />
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div> */}

                      {/* Action Buttons */}
                      <div className="list-group-item bg-light border-top">
                        <div className="d-flex justify-content-end gap-2 mt-3">
                          <button
                            className="btn btn-outline-secondary btn-sm px-4"
                            onClick={cancelGeneralEditing}
                            disabled={loading}
                          >
                            <i className="fas fa-times me-1"></i>
                            Cancel
                          </button>
                          <button
                            className="btn btn-sm px-4"
                            style={{
                              backgroundColor: themeColor,
                              color: 'white',
                              border: 'none',
                              transition: 'all 0.3s ease'
                            }}
                            onClick={saveGeneralSettings}
                            disabled={loading}
                          >
                            {loading ? (
                              <>
                                <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                                Saving...
                              </>
                            ) : (
                              <>
                                <i className="fas fa-save me-1"></i>
                                Save Changes
                              </>
                            )}
                          </button>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>

            {renderTaxSection()}

            {!HIDE_TABLE_RESERVATION && (
            <div id="TableReservation" className="mb-4 pb-3">
              <h4 className="d-flex align-items-center mb-2">
                <i className="fas fa-calendar-check me-2" style={{ color: themeColor }}></i>
                Table Reservation
              </h4>
              <p>Configure table reservation settings.</p>
              <div className="card">
                <div className="list-group list-group-flush fw-bold">
                  {!isTableReservationEditing ? (
                    <>
                      {/* First Row - Margin Time and Release Time */}
                      <div className="list-group-item">
                        <div className="row g-3">
                          <div className="col-md-6">
                            <div className="d-flex align-items-center mb-1">
                              <i className="fas fa-clock me-2" style={{ color: themeColor }}></i>
                              <span className="fw-semibold">Margin Time (min)</span>
                            </div>
                            <div className="text-muted">
                              <span className="badge" style={{ backgroundColor: '#6c757d' }}>
                                {formState.s_TableReservationMarginTime ? `${formState.s_TableReservationMarginTime} minutes` : "Not set"}
                              </span>
                            </div>
                          </div>
                          <div className="col-md-6">
                            <div className="d-flex align-items-center mb-1">
                              <i className="fas fa-hourglass-half me-2" style={{ color: themeColor }}></i>
                              <span className="fw-semibold">Release Time (min)</span>
                            </div>
                            <div className="text-muted">
                              <span className="badge" style={{ backgroundColor: '#6c757d' }}>
                                {formState.s_TableReservedReleaseTime ? `${formState.s_TableReservedReleaseTime} minutes` : "Not set"}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Second Row - Reserved Table Status Change */}
                      <div className="list-group-item">
                        <div className="row g-3">
                          <div className="col-md-6">
                            <div className="d-flex align-items-center mb-1">
                              <i className="fas fa-exchange-alt me-2" style={{ color: themeColor }}></i>
                              <span className="fw-semibold">Reserved Table Status Change</span>
                            </div>
                            <div className="text-muted">
                              <span className="badge" style={{ backgroundColor: '#6c757d' }}>
                                {formState.s_ShowingReserveredTableTime ? `${formState.s_ShowingReserveredTableTime} minutes` : "Not set"}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Single Edit Button */}
                      <div className="list-group-item d-flex justify-content-end">
                        <button
                          className="btn btn-secondary w-100px"
                          onClick={startTableReservationEditing}
                        >
                          Edit
                        </button>
                      </div>
                    </>
                  ) : (
                    <>
                      {/* First Row - Margin Time and Release Time */}
                      <div className="list-group-item">
                        <div className="row g-3">
                          <div className="col-md-6">
                            <div className="d-flex align-items-center mb-2">
                              <i className="fas fa-clock me-2" style={{ color: themeColor }}></i>
                              <span className="fw-semibold">Margin Time (min)</span>
                            </div>
                            <FormInput
                              type="number"
                              value={formState.s_TableReservationMarginTime}
                              onchange={(e) => {
                                const sanitizedValue = isNaN(Number(e.target.value)) || e.target.value === ""
                                  ? ""
                                  : Number(e.target.value);

                                if (typeof sanitizedValue === "number" && sanitizedValue < 0) {
                                  showErrorToast("Margin Time must be a positive number");
                                  return;
                                }

                                setFormState((prev) => ({
                                  ...prev,
                                  s_TableReservationMarginTime: sanitizedValue,
                                }));
                              }}
                              placeHolder="Enter margin time in minutes"
                              name="s_TableReservationMarginTime"
                              min="0"
                            />
                          </div>
                          <div className="col-md-6">
                            <div className="d-flex align-items-center mb-2">
                              <i className="fas fa-hourglass-half me-2" style={{ color: themeColor }}></i>
                              <span className="fw-semibold">Release Time (min)</span>
                            </div>
                            <FormInput
                              type="number"
                              value={formState.s_TableReservedReleaseTime}
                              onchange={(e) => {
                                const sanitizedValue = isNaN(Number(e.target.value)) || e.target.value === ""
                                  ? ""
                                  : Number(e.target.value);

                                if (typeof sanitizedValue === "number" && sanitizedValue < 0) {
                                  showErrorToast("Release Time must be a positive number");
                                  return;
                                }

                                setFormState((prev) => ({
                                  ...prev,
                                  s_TableReservedReleaseTime: sanitizedValue,
                                }));
                              }}
                              placeHolder="Enter release time in minutes"
                              name="s_TableReservedReleaseTime"
                              min="0"
                            />
                          </div>
                        </div>
                      </div>

                      {/* Second Row - Reserved Table Status Change */}
                      <div className="list-group-item">
                        <div className="row g-3">
                          <div className="col-md-6">
                            <div className="d-flex align-items-center mb-2">
                              <i className="fas fa-exchange-alt me-2" style={{ color: themeColor }}></i>
                              <span className="fw-semibold">Reserved Table Status Change</span>
                            </div>
                            <FormInput
                              type="number"
                              value={formState.s_ShowingReserveredTableTime}
                              onchange={(e) => {
                                const sanitizedValue = isNaN(Number(e.target.value)) || e.target.value === ""
                                  ? ""
                                  : Number(e.target.value);

                                if (typeof sanitizedValue === "number" && sanitizedValue < 0) {
                                  showErrorToast("Reserved Table Status Change must be a positive number");
                                  return;
                                }

                                setFormState((prev) => ({
                                  ...prev,
                                  s_ShowingReserveredTableTime: sanitizedValue,
                                }));
                              }}
                              placeHolder="Enter status change time in minutes"
                              name="s_ShowingReserveredTableTime"
                              min="0"
                            />
                          </div>
                        </div>
                      </div>

                      {/* Action Buttons */}
                      <div className="list-group-item bg-light border-top">
                        <div className="d-flex justify-content-end gap-2 mt-3">
                          <button
                            className="btn btn-outline-secondary btn-sm px-4"
                            onClick={cancelTableReservationEditing}
                            disabled={loading}
                          >
                            <i className="fas fa-times me-1"></i>
                            Cancel
                          </button>
                          <button
                            className="btn btn-sm px-4"
                            style={{
                              backgroundColor: themeColor,
                              color: 'white',
                              border: 'none',
                              transition: 'all 0.3s ease'
                            }}
                            onClick={saveTableReservationSettings}
                            disabled={loading}
                          >
                            {loading ? (
                              <>
                                <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                                Saving...
                              </>
                            ) : (
                              <>
                                <i className="fas fa-save me-1"></i>
                                Save Changes
                              </>
                            )}
                          </button>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
            )}

            {/* <div id="Kitchen" className="mb-4 pb-3">
              <h4 className="d-flex align-items-center mb-2">
                <i className="fas fa-utensils me-2" style={{ color: themeColor }}></i>
                Kitchen
              </h4>
              <p>Manage kitchen-related settings.</p>
              <div className="card">
                <div className="list-group list-group-flush fw-bold">
                  {!isKitchenEditing ? (
                    <>
      
                      <div className="list-group-item d-flex align-items-center">
                        <div className="flex-fill">
                          <div className="d-flex align-items-center mb-1">
                            <i className="fas fa-sync me-2" style={{ color: themeColor }}></i>
                            <span className="fw-semibold">Update from Kitchen</span>
                          </div>
                          <div className="text-muted">
                            <span className={`badge ${formState.s_IsUpdatefromKitchen ? 'bg-success' : 'bg-secondary'}`}>
                              {formState.s_IsUpdatefromKitchen ? "Enabled" : "Disabled"}
                            </span>
                          </div>
                        </div>
                      </div>


                      <div className="list-group-item d-flex align-items-center">
                        <div className="flex-fill">
                          <div className="d-flex align-items-center mb-1">
                            <i className="fas fa-clock me-2" style={{ color: themeColor }}></i>
                            <span className="fw-semibold">Show Cancelled Order Duration (min)</span>
                          </div>
                          <div className="text-muted">
                            <span className="badge bg-success">
                              {formState.s_ShowCancelledOrderDuration ? `${formState.s_ShowCancelledOrderDuration} minutes` : "Not set"}
                            </span>
                          </div>
                        </div>
                      </div>

        
                      <div className="list-group-item d-flex align-items-center">
                        <div className="flex-fill">
                          <div className="d-flex align-items-center mb-1">
                            <i className="fas fa-calendar-times me-2" style={{ color: themeColor }}></i>
                            <span className="fw-semibold">Visible Cancel Order Days in Cancel Tab</span>
                          </div>
                          <div className="text-muted">
                            <span className="badge bg-success">
                              {formState.s_VisibleCancelOrderDaysinCancelTab ? `${formState.s_VisibleCancelOrderDaysinCancelTab} days` : "Not set"}
                            </span>
                          </div>
                        </div>
                      </div>

              
                      <div className="list-group-item d-flex justify-content-end">
                        <button
                          className="btn btn-secondary w-100px"
                          onClick={startKitchenEditing}
                        >
                          Edit
                        </button>
                      </div>
                    </>
                  ) : (
                    <>
                  
                      <div className="list-group-item">
                        <div className="d-flex align-items-center mb-2">
                          <i className="fas fa-sync text-info me-2"></i>
                          <span className="fw-semibold">Update from Kitchen</span>
                        </div>
                        <div className="mt-2">
                          <ToggleSwitch
                            isChecked={formState.s_IsUpdatefromKitchen}
                            onToggle={(e) => {
                              setFormState((prev) => ({
                                ...prev,
                                s_IsUpdatefromKitchen: e.target?.checked ?? e,
                              }));
                            }}
                            label="Update from Kitchen"
                            name="s_IsUpdatefromKitchen"
                            themeColor={themeColor}
                          />
                        </div>
                      </div>

              
                      <div className="list-group-item">
                        <div className="d-flex align-items-center mb-2">
                            <i className="fas fa-clock me-2" style={{ color: themeColor }}></i>
                          <span className="fw-semibold">Show Cancelled Order Duration (min)</span>
                        </div>
                        <div className="mt-2">
                          <FormInput
                            type="number"
                            value={formState.s_ShowCancelledOrderDuration}
                            onchange={(e) => {
                              const sanitizedValue = isNaN(Number(e.target.value)) || e.target.value === ""
                                ? ""
                                : Number(e.target.value);

                              if (typeof sanitizedValue === "number" && sanitizedValue < 0) {
                                showErrorToast("Show Cancelled Order Duration must be a positive number");
                                return;
                              }

                              setFormState((prev) => ({
                                ...prev,
                                s_ShowCancelledOrderDuration: sanitizedValue,
                              }));
                            }}
                            placeHolder="Enter duration in minutes"
                            name="s_ShowCancelledOrderDuration"
                            min="0"
                          />
                        </div>
                      </div>

                      <div className="list-group-item">
                        <div className="d-flex align-items-center mb-2">
                            <i className="fas fa-calendar-times me-2" style={{ color: themeColor }}></i>
                          <span className="fw-semibold">Visible Cancel Order Days in Cancel Tab</span>
                        </div>
                        <div className="mt-2">
                          <FormInput
                            type="number"
                            value={formState.s_VisibleCancelOrderDaysinCancelTab}
                            onchange={(e) => {
                              const sanitizedValue = isNaN(Number(e.target.value)) || e.target.value === ""
                                ? ""
                                : Number(e.target.value);

                              if (typeof sanitizedValue === "number" && sanitizedValue < 0) {
                                showErrorToast("Visible Cancel Order Days must be a positive number");
                                return;
                              }

                              setFormState((prev) => ({
                                ...prev,
                                s_VisibleCancelOrderDaysinCancelTab: sanitizedValue,
                              }));
                            }}
                            placeHolder="Enter number of days"
                            name="s_VisibleCancelOrderDaysinCancelTab"
                            min="0"
                          />
                        </div>
                      </div>

   
                      <div className="list-group-item bg-light border-top">
                        <div className="d-flex justify-content-end gap-2 mt-3">
                          <button
                            className="btn btn-outline-secondary btn-sm px-4"
                            onClick={cancelKitchenEditing}
                            disabled={loading}
                          >
                            <i className="fas fa-times me-1"></i>
                            Cancel
                          </button>
                          <button
                            className="btn btn-sm px-4"
                            style={{
                              backgroundColor: themeColor,
                              color: 'white',
                              border: 'none',
                              transition: 'all 0.3s ease'
                            }}
                            onClick={saveKitchenSettings}
                            disabled={loading}
                          >
                            {loading ? (
                              <>
                                <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                                Saving...
                              </>
                            ) : (
                              <>
                                <i className="fas fa-save me-1"></i>
                                Save Changes
                              </>
                            )}
                          </button>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div> */}

            {!HIDE_LOYALTY_OPTION && (
            <div id="LoyaltyOption" className="mb-4 pb-3">
              <h4 className="d-flex align-items-center mb-2">
                <i className="fas fa-star me-2" style={{ color: themeColor }}></i>
                Loyalty Option
              </h4>
              <p>Configure loyalty program settings.</p>
              <div className="card">
                <div className="list-group list-group-flush fw-bold">
                  {!isLoyaltyEditing ? (
                    <>
                      {/* Loyalty Status - View Mode */}
                      <div className="list-group-item d-flex align-items-center">
                        <div className="flex-fill">
                          <div className="d-flex align-items-center mb-1">
                            <i className="fas fa-gift me-2" style={{ color: themeColor }}></i>
                            <span className="fw-semibold">Loyalty Status</span>
                          </div>
                          <div className="text-muted">
                            <span className={`badge ${formState.s_IsHaveLoyalty ? '' : 'bg-secondary'}`} style={{ backgroundColor: '#6c757d' }}>
                              {formState.s_IsHaveLoyalty ? "Enabled" : "Disabled"}
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Points Per Purchase - View Mode */}
                      <div className="list-group-item d-flex align-items-center">
                        <div className="flex-fill">
                          <div className="d-flex align-items-center mb-1">
                            <i className="fas fa-coins me-2" style={{ color: themeColor }}></i>
                            <span className="fw-semibold">Points Per Purchase</span>
                          </div>
                          <div className="text-muted">
                            <span className="badge" style={{ backgroundColor: '#6c757d' }}>
                              {formState.s_PointperPurchase ? `${formState.s_PointperPurchase} points` : "Not set"}
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Points Per Dollar - View Mode */}
                      <div className="list-group-item d-flex align-items-center">
                        <div className="flex-fill">
                          <div className="d-flex align-items-center mb-1">
                            <i className="fas fa-dollar-sign me-2" style={{ color: themeColor }}></i>
                            <span className="fw-semibold">Points Per Dollar</span>
                          </div>
                          <div className="text-muted">
                            <span className="badge" style={{ backgroundColor: '#6c757d' }}>
                              {formState.s_PointSperDollar ? `${formState.s_PointSperDollar} points` : "Not set"}
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Single Edit Button */}
                      <div className="list-group-item d-flex justify-content-end">
                        <button
                          className="btn btn-secondary w-100px"
                          onClick={startLoyaltyEditing}
                        >
                          Edit
                        </button>
                      </div>
                    </>
                  ) : (
                    <>
                      {/* Loyalty Status - Edit Mode */}
                      <div className="list-group-item">
                        <div className="d-flex align-items-center mb-2">
                          <i className="fas fa-gift me-2" style={{ color: themeColor }}></i>
                          <span className="fw-semibold">Loyalty Status</span>
                        </div>
                        <div className="mt-2">
                          <ToggleSwitch
                            isChecked={formState.s_IsHaveLoyalty}
                            onToggle={(e) => {
                              setFormState((prev) => ({
                                ...prev,
                                s_IsHaveLoyalty: e.target?.checked ?? e,
                              }));
                            }}
                            label="Loyalty Status"
                            name="s_IsHaveLoyalty"
                            themeColor={themeColor}
                          />
                        </div>
                      </div>

                      {/* Points Per Purchase - Edit Mode */}
                      <div className="list-group-item">
                        <div className="d-flex align-items-center mb-2">
                          <i className="fas fa-coins me-2" style={{ color: themeColor }}></i>
                          <span className="fw-semibold">Points Per Purchase</span>
                        </div>
                        <div className="mt-2">
                          <FormInput
                            type="number"
                            value={formState.s_PointperPurchase}
                            onchange={(e) => {
                              const sanitizedValue = isNaN(Number(e.target.value)) || e.target.value === ""
                                ? ""
                                : Number(e.target.value);

                              if (typeof sanitizedValue === "number" && sanitizedValue < 0) {
                                showErrorToast("Points Per Purchase must be a positive number");
                                return;
                              }

                              setFormState((prev) => ({
                                ...prev,
                                s_PointperPurchase: sanitizedValue,
                              }));
                            }}
                            placeHolder="Enter points per purchase"
                            name="s_PointperPurchase"
                            min="0"
                          />
                        </div>
                      </div>

                      {/* Points Per Dollar - Edit Mode */}
                      <div className="list-group-item">
                        <div className="d-flex align-items-center mb-2">
                          <i className="fas fa-dollar-sign me-2" style={{ color: themeColor }}></i>
                          <span className="fw-semibold">Points Per Dollar</span>
                        </div>
                        <div className="mt-2">
                          <FormInput
                            type="number"
                            value={formState.s_PointSperDollar}
                            onchange={(e) => {
                              const sanitizedValue = isNaN(Number(e.target.value)) || e.target.value === ""
                                ? ""
                                : Number(e.target.value);

                              if (typeof sanitizedValue === "number" && sanitizedValue < 0) {
                                showErrorToast("Points Per Dollar must be a positive number");
                                return;
                              }

                              setFormState((prev) => ({
                                ...prev,
                                s_PointSperDollar: sanitizedValue,
                              }));
                            }}
                            placeHolder="Enter points per dollar"
                            name="s_PointSperDollar"
                            min="0"
                          />
                        </div>
                      </div>

                      {/* Action Buttons */}
                      <div className="list-group-item bg-light border-top">
                        <div className="d-flex justify-content-end gap-2 mt-3">
                          <button
                            className="btn btn-outline-secondary btn-sm px-4"
                            onClick={cancelLoyaltyEditing}
                            disabled={loading}
                          >
                            <i className="fas fa-times me-1"></i>
                            Cancel
                          </button>
                          <button
                            className="btn btn-sm px-4"
                            style={{
                              backgroundColor: themeColor,
                              color: 'white',
                              border: 'none',
                              transition: 'all 0.3s ease'
                            }}
                            onClick={saveLoyaltySettings}
                            disabled={loading}
                          >
                            {loading ? (
                              <>
                                <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                                Saving...
                              </>
                            ) : (
                              <>
                                <i className="fas fa-save me-1"></i>
                                Save Changes
                              </>
                            )}
                          </button>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
            )}

            {!HIDE_ORDERS_TRACKING_TV && (
            <div id="ChooseModeforOrdersTrackingTVApp" className="mb-4 pb-3">
              <h4 className="d-flex align-items-center mb-2">
                <i className="fas fa-tv me-2" style={{ color: themeColor }}></i>
                Orders Tracking TV
              </h4>
              <p>Choose modes for the orders tracking TV app.</p>
              <div className="card">
                <div className="list-group list-group-flush fw-bold">
                  {!isTVAppEditing ? (
                    <>
                      {/* Order Status - View Mode */}
                      <div className="list-group-item d-flex align-items-center">
                        <div className="flex-fill">
                          <div className="d-flex align-items-center mb-1">
                            <i className="fas fa-info-circle me-2" style={{ color: themeColor }}></i>
                            <span className="fw-semibold">Order Status</span>
                          </div>
                          <div className="text-muted">
                            <span className={`badge ${formState.tV_ISOrderStatus ? '' : 'bg-secondary'}`} style={{ backgroundColor: '#6c757d' }}>
                              {formState.tV_ISOrderStatus ? "Enabled" : "Disabled"}
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Order Type - View Mode */}
                      <div className="list-group-item d-flex align-items-center">
                        <div className="flex-fill">
                          <div className="d-flex align-items-center mb-1">
                            <i className="fas fa-tags me-2" style={{ color: themeColor }}></i>
                            <span className="fw-semibold">Order Type</span>
                          </div>
                          <div className="text-muted">
                            <span className={`badge ${formState.tV_ISOrderType ? '' : 'bg-secondary'}`} style={{ backgroundColor: '#6c757d' }}>
                              {formState.tV_ISOrderType ? "Enabled" : "Disabled"}
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Order Number - View Mode */}
                      <div className="list-group-item d-flex align-items-center">
                        <div className="flex-fill">
                          <div className="d-flex align-items-center mb-1">
                            <i className="fas fa-hashtag me-2" style={{ color: themeColor }}></i>
                            <span className="fw-semibold">Order Number</span>
                          </div>
                          <div className="text-muted">
                            <span className={`badge ${formState.tV_ISOrderNumber ? '' : 'bg-secondary'}`} style={{ backgroundColor: '#6c757d' }}>
                              {formState.tV_ISOrderNumber ? "Enabled" : "Disabled"}
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Order Owner - View Mode */}
                      <div className="list-group-item d-flex align-items-center">
                        <div className="flex-fill">
                          <div className="d-flex align-items-center mb-1">
                            <i className="fas fa-user me-2" style={{ color: themeColor }}></i>
                            <span className="fw-semibold">Order Owner</span>
                          </div>
                          <div className="text-muted">
                            <span className={`badge ${formState.tV_ISOrderOwner ? '' : 'bg-secondary'}`} style={{ backgroundColor: '#6c757d' }}>
                              {formState.tV_ISOrderOwner ? "Enabled" : "Disabled"}
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Single Edit Button */}
                      <div className="list-group-item d-flex justify-content-end">
                        <button
                          className="btn btn-secondary w-100px"
                          onClick={startTVAppEditing}
                        >
                          Edit
                        </button>
                      </div>
                    </>
                  ) : (
                    <>
                      {/* Order Status - Edit Mode */}
                      <div className="list-group-item">
                        <div className="d-flex align-items-center mb-2">
                          <i className="fas fa-info-circle me-2" style={{ color: themeColor }}></i>
                          <span className="fw-semibold">Order Status</span>
                        </div>
                        <div className="mt-2">
                          <ToggleSwitch
                            isChecked={formState.tV_ISOrderStatus}
                            onToggle={(e) => {
                              setFormState((prev) => ({
                                ...prev,
                                tV_ISOrderStatus: e.target?.checked ?? e,
                              }));
                            }}
                            label="Order Status"
                            name="tV_ISOrderStatus"
                            themeColor={themeColor}
                          />
                        </div>
                      </div>

                      {/* Order Type - Edit Mode */}
                      <div className="list-group-item">
                        <div className="d-flex align-items-center mb-2">
                          <i className="fas fa-tags me-2" style={{ color: themeColor }}></i>
                          <span className="fw-semibold">Order Type</span>
                        </div>
                        <div className="mt-2">
                          <ToggleSwitch
                            isChecked={formState.tV_ISOrderType}
                            onToggle={(e) => {
                              setFormState((prev) => ({
                                ...prev,
                                tV_ISOrderType: e.target?.checked ?? e,
                              }));
                            }}
                            label="Order Type"
                            name="tV_ISOrderType"
                            themeColor={themeColor}
                          />
                        </div>
                      </div>

                      {/* Order Number - Edit Mode */}
                      <div className="list-group-item">
                        <div className="d-flex align-items-center mb-2">
                          <i className="fas fa-hashtag me-2" style={{ color: themeColor }}></i>
                          <span className="fw-semibold">Order Number</span>
                        </div>
                        <div className="mt-2">
                          <ToggleSwitch
                            isChecked={formState.tV_ISOrderNumber}
                            onToggle={(e) => {
                              setFormState((prev) => ({
                                ...prev,
                                tV_ISOrderNumber: e.target?.checked ?? e,
                              }));
                            }}
                            label="Order Number"
                            name="tV_ISOrderNumber"
                            themeColor={themeColor}
                          />
                        </div>
                      </div>

                      {/* Order Owner - Edit Mode */}
                      <div className="list-group-item">
                        <div className="d-flex align-items-center mb-2">
                          <i className="fas fa-user me-2" style={{ color: themeColor }}></i>
                          <span className="fw-semibold">Order Owner</span>
                        </div>
                        <div className="mt-2">
                          <ToggleSwitch
                            isChecked={formState.tV_ISOrderOwner}
                            onToggle={(e) => {
                              setFormState((prev) => ({
                                ...prev,
                                tV_ISOrderOwner: e.target?.checked ?? e,
                              }));
                            }}
                            label="Order Owner"
                            name="tV_ISOrderOwner"
                            themeColor={themeColor}
                          />
                        </div>
                      </div>

                      {/* Action Buttons */}
                      <div className="list-group-item bg-light border-top">
                        <div className="d-flex justify-content-end gap-2 mt-3">
                          <button
                            className="btn btn-outline-secondary btn-sm px-4"
                            onClick={cancelTVAppEditing}
                            disabled={loading}
                          >
                            <i className="fas fa-times me-1"></i>
                            Cancel
                          </button>
                          <button
                            className="btn btn-sm px-4"
                            style={{
                              backgroundColor: themeColor,
                              color: 'white',
                              border: 'none',
                              transition: 'all 0.3s ease'
                            }}
                            onClick={saveTVAppSettings}
                            disabled={loading}
                          >
                            {loading ? (
                              <>
                                <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                                Saving...
                              </>
                            ) : (
                              <>
                                <i className="fas fa-save me-1"></i>
                                Save Changes
                              </>
                            )}
                          </button>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
            )}

            <div id="ServiceHours" className="mb-4 pb-3">
              <h4 className="d-flex align-items-center mb-2">
                <i className="fas fa-clock me-2" style={{ color: themeColor }}></i>
                Service Hours
              </h4>
              <p>Manage your company service hours.</p>

              {/* Restaurant Hours Card */}
              <div className="card mb-3">
                <div className="card-body">
                  <div className="d-flex align-items-center mb-3">
                    <div className="flex-fill">
                      <div className="d-flex align-items-center mb-2">
                        <i className="fas fa-utensils me-2" style={{ color: themeColor }}></i>
                        <h5 className="mb-0 fw-semibold">Restaurant Hours</h5>
                      </div>
                      {renderServiceHoursDisplay(
                        "weeklyServiceHours",
                        timingData.weeklyServiceHours,
                        "Restaurant Hours"
                      )}
                    </div>
                  </div>
                  {editingField !== "weeklyServiceHours" && (
                    <div className="d-flex justify-content-end">
                      <button
                        className="btn btn-secondary w-100px"
                        onClick={() => startEditing("weeklyServiceHours", timingData.weeklyServiceHours)}
                      >
                        Edit
                      </button>
                    </div>
                  )}
                </div>

                {/* Edit Mode for Restaurant Hours - Show below Restaurant Hours card */}
                {editingField === "weeklyServiceHours" && (
                  <div className="card-footer border-top-0 bg-light">
                    <div className="card"
                      style={{ borderColor: themeColor, borderWidth: '2px' }}>
                      <div className="card-body">
                        <div className="d-flex align-items-center mb-3">
                          <i className="fas fa-utensils me-2" style={{ color: themeColor }}></i>
                          <h5 className="mb-0 fw-semibold">Edit Restaurant Hours</h5>
                        </div>

                        <div className="mb-4">
                          <ToggleSwitch
                            isChecked={enableSchedule.weeklyServiceHours}
                            onToggle={() =>
                              setEnableSchedule((prev) => ({
                                ...prev,
                                weeklyServiceHours: !prev.weeklyServiceHours,
                              }))
                            }
                            label="Set Restaurant Hours"
                            name="enableSchedule-weeklyServiceHours"
                            themeColor={themeColor}
                          />
                          <p className="text-muted mt-2">
                            Set the days of the week and times of day for restaurant hours.
                          </p>
                        </div>

                        {enableSchedule.weeklyServiceHours && (
                          <div className="mb-4">
                            <div className="row">
                              <div className="col-12">
                                <ScheduleManager
                                  schedules={schedules.weeklyServiceHours}
                                  setSchedules={(updatedSchedules) =>
                                    setSchedules((prev) => ({
                                      ...prev,
                                      weeklyServiceHours: updatedSchedules,
                                    }))
                                  }
                                  isProfilePage={true}
                                  isDarkMode={isDarkMode}
                                  themeColor={themeColor}
                                />
                              </div>
                            </div>
                          </div>
                        )}

                        <div className="d-flex justify-content-end gap-2 mt-3">
                          <button
                            className="btn btn-outline-secondary btn-sm px-4"
                            onClick={cancelEditing}
                            disabled={loading}
                          >
                            <i className="fas fa-times me-1"></i>
                            Cancel
                          </button>
                          <button
                            className="btn btn-sm px-4"
                            style={{
                              backgroundColor: themeColor,
                              color: 'white',
                              border: 'none',
                              transition: 'all 0.3s ease'
                            }}
                            onClick={() => saveField("weeklyServiceHours")}
                            disabled={loading}
                          >
                            {loading ? (
                              <>
                                <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                                Saving...
                              </>
                            ) : (
                              <>
                                <i className="fas fa-save me-1"></i>
                                Save Changes
                              </>
                            )}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div id="PreOrderingModule" className="mb-4 pb-3">
              <h4 className="d-flex align-items-center mb-2">
                <i className="fas fa-shopping-cart me-2" style={{ color: themeColor }}></i>
                Online Ordering
              </h4>
              <p>Configure Pre-Ordering, Collection, Delivery.</p>
              <div className="card">
                <div className="list-group list-group-flush fw-bold">
                  {!isOnlineOrderingEditing ? (
                    <>
                      <div className="list-group-item d-flex align-items-center">
                        <div className="flex-fill">
                          <div>Pre-Ordering</div>
                          <div className="text-muted">
                            {formState.isActivePreOrderModule ? "Enabled" : "Disabled"}
                          </div>
                        </div>
                      </div>
                      {/* Pre-Ordering Collection and Delivery - View Mode - Only show when Pre-Ordering is enabled */}
                      {formState.isActivePreOrderModule && (
                        <div className="list-group-item">
                          <div className="row">
                            <div className="col-md-6">
                              <div className="d-flex align-items-center">
                                <div className="flex-fill">
                                  <div className="d-flex align-items-center mb-1">
                                    <i className="fas fa-shopping-cart me-2" style={{ color: themeColor }}></i>
                                    <span className="fw-semibold">Pre-Ordering Collection</span>
                                  </div>
                                  <div className="text-muted">
                                    <span className={`badge ${formState.preOrderingCollection ? '' : 'bg-secondary'}`} style={{ backgroundColor: '#6c757d' }}>
                                      {formState.preOrderingCollection ? "Enabled" : "Disabled"}
                                    </span>
                                  </div>
                                </div>
                              </div>
                            </div>
                            <div className="col-md-6">
                              <div className="d-flex align-items-center">
                                <div className="flex-fill">
                                  <div className="d-flex align-items-center mb-1">
                                    <i className="fas fa-truck me-2" style={{ color: themeColor }}></i>
                                    <span className="fw-semibold">Pre-Ordering Delivery</span>
                                  </div>
                                  <div className="text-muted">
                                    <span className={`badge ${formState.preOrderingDelivery ? '' : 'bg-secondary'}`} style={{ backgroundColor: '#6c757d' }}>
                                      {formState.preOrderingDelivery ? "Enabled" : "Disabled"}
                                    </span>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Pre-Order Day Range - View Mode - Only show when Pre-Ordering is enabled */}
                      {formState.isActivePreOrderModule && (
                        <div className="list-group-item d-flex align-items-center">
                          <div className="flex-fill">
                            <div className="d-flex align-items-center mb-1">
                              <i className="fas fa-calendar-alt me-2" style={{ color: themeColor }}></i>
                              <span className="fw-semibold">Pre-Order Day Range</span>
                            </div>
                            <div className="text-muted">
                              <span className="badge" style={{ backgroundColor: '#6c757d' }}>
                                {preOrderOptions.find(opt => opt.value === formState.preOrderDayRange)?.label || "Not set"}
                              </span>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Pre Order Alert within Minutes - View Mode - Only show when Pre-Ordering is enabled */}
                      {formState.isActivePreOrderModule && (
                        <div className="list-group-item d-flex align-items-center">
                          <div className="flex-fill">
                            <div className="d-flex align-items-center mb-1">
                              <i className="fas fa-clock me-2" style={{ color: themeColor }}></i>
                              <span className="fw-semibold">Pre Order Alert within Minutes</span>
                            </div>
                            <div className="text-muted">
                              <span className="badge" style={{ backgroundColor: '#6c757d' }}>
                                {formState.preOrderAlertInMins ? `${formState.preOrderAlertInMins} minutes` : "Not set"}
                              </span>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* General Collection and Delivery - View Mode */}
                      <div className="list-group-item bg-light">
                        <div className="d-flex align-items-center mb-3">
                          <i className="fas fa-cogs me-2" style={{ color: themeColor }}></i>
                          <span className="fw-bold text-dark">General Order Settings</span>
                        </div>
                        <div className="row">
                          <div className="col-md-6">
                            <div className="d-flex align-items-center">
                              <div className="flex-fill">
                                <div className="d-flex align-items-center mb-1">
                                  <i className="fas fa-store me-2" style={{ color: themeColor }}></i>
                                  <span className="fw-semibold">Collection</span>
                                </div>
                                <div className="text-muted">
                                  <span className="badge" style={{ backgroundColor: '#6c757d' }}>
                                    {formState.compCollectionStatus ? "Active" : "Inactive"}
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>
                          <div className="col-md-6">
                            <div className="d-flex align-items-center">
                              <div className="flex-fill">
                                <div className="d-flex align-items-center mb-1">
                                  <i className="fas fa-shipping-fast me-2" style={{ color: themeColor }}></i>
                                  <span className="fw-semibold">Delivery</span>
                                </div>
                                <div className="text-muted">
                                  <span className="badge" style={{ backgroundColor: '#6c757d' }}>
                                    {formState.compDeliveryStatus ? "Active" : "Inactive"}
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Single Edit Button */}
                      <div className="list-group-item d-flex justify-content-end">
                        <button
                          className="btn btn-secondary w-100px"
                          onClick={startOnlineOrderingEditing}
                        >
                          Edit
                        </button>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="list-group-item">
                        <div className="d-flex align-items-center mb-2">
                          <i className="fas fa-shopping-bag text-primary me-2"></i>
                          <span className="fw-semibold">Pre-Ordering</span>
                        </div>
                        <div className="mt-2">
                          <ToggleSwitch
                            isChecked={formState.isActivePreOrderModule}
                            onToggle={(e) => {
                              setFormState((prev) => ({
                                ...prev,
                                isActivePreOrderModule: e.target?.checked ?? e,
                              }));
                            }}
                            label="Pre-Ordering"
                            name="isActivePreOrderModule"
                            themeColor={themeColor}
                          />
                        </div>
                      </div>
                      {/* Pre-Ordering Collection and Delivery - Only show when Pre-Ordering is enabled */}
                      {formState.isActivePreOrderModule && (
                        <div className="list-group-item">
                          <div className="row">
                            <div className="col-md-6">
                              <div className="d-flex align-items-center">
                                <div className="flex-fill">
                                  <div className="d-flex align-items-center mb-2">
                                    <i className="fas fa-shopping-cart me-2" style={{ color: themeColor }}></i>
                                    <span className="fw-semibold">Pre-Ordering Collection</span>
                                  </div>
                                  <div className="mt-2">
                                    <ToggleSwitch
                                      isChecked={formState.preOrderingCollection}
                                      onToggle={(e) => {
                                        setFormState((prev) => ({
                                          ...prev,
                                          preOrderingCollection: e.target?.checked ?? e,
                                        }));
                                      }}
                                      label="Collection"
                                      name="preOrderingCollection"
                                      themeColor={themeColor}
                                    />
                                  </div>
                                </div>
                              </div>
                            </div>
                            <div className="col-md-6">
                              <div className="d-flex align-items-center">
                                <div className="flex-fill">
                                  <div className="d-flex align-items-center mb-2">
                                    <i className="fas fa-truck me-2" style={{ color: themeColor }}></i>
                                    <span className="fw-semibold">Pre-Ordering Delivery</span>
                                  </div>
                                  <div className="mt-2">
                                    <ToggleSwitch
                                      isChecked={formState.preOrderingDelivery}
                                      onToggle={(e) => {
                                        setFormState((prev) => ({
                                          ...prev,
                                          preOrderingDelivery: e.target?.checked ?? e,
                                        }));
                                      }}
                                      label="Delivery"
                                      name="preOrderingDelivery"
                                      themeColor={themeColor}
                                    />
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Pre-Order Day Range - Only show when Pre-Ordering is enabled */}
                      {formState.isActivePreOrderModule && (
                        <div className="list-group-item">
                          <div className="d-flex align-items-center mb-2">
                            <i className="fas fa-calendar-alt me-2" style={{ color: themeColor }}></i>
                            <span className="fw-semibold  ">Pre-Order Day Range</span>
                          </div>
                          <div className="mt-2">
                            <Select
                              options={preOrderOptions}
                              value={preOrderOptions.find((o) => o.value === formState.preOrderDayRange)}
                              onChange={(option) => {
                                setFormState((prev) => ({
                                  ...prev,
                                  preOrderDayRange: option ? option.value : "",
                                }));
                              }}
                              placeholder="Select Pre-Order Day Range"
                              isClearable
                              className="react-select-container"
                              classNamePrefix="react-select"
                            />
                          </div>
                        </div>
                      )}

                      {/* Pre Order Alert within Minutes - Only show when Pre-Ordering is enabled */}
                      {formState.isActivePreOrderModule && (
                        <div className="list-group-item">
                          <div className="d-flex align-items-center mb-2">
                            <i className="fas fa-clock me-2" style={{ color: themeColor }}></i>
                            <span className="fw-semibold text-success">Pre Order Alert within Minutes</span>
                          </div>
                          <div className="mt-2">
                            <FormInput
                              type="number"
                              value={formState.preOrderAlertInMins}
                              onchange={(e) => {
                                const sanitizedValue = isNaN(Number(e.target.value)) || e.target.value === ""
                                  ? ""
                                  : Number(e.target.value);

                                if (typeof sanitizedValue === "number" && sanitizedValue < 0) {
                                  showErrorToast("Pre Order Alert Minutes must be a positive number");
                                  return;
                                }

                                setFormState((prev) => ({
                                  ...prev,
                                  preOrderAlertInMins: sanitizedValue,
                                }));
                              }}
                              placeHolder="Enter minutes for pre-order alert"
                              name="preOrderAlertInMins"
                              min="0"
                              className="text-success"
                            />
                          </div>
                        </div>
                      )}
                      {/* General Collection and Delivery Settings */}
                      <div className="list-group-item bg-light">
                        <div className="d-flex align-items-center mb-3">
                          <i className="fas fa-cogs text-secondary me-2"></i>
                          <span className="fw-bold text-dark">General Order Settings</span>
                        </div>
                        <div className="row">
                          <div className="col-md-6">
                            <div className="d-flex align-items-center">
                              <div className="flex-fill">
                                <div className="d-flex align-items-center mb-2">
                                  <i className="fas fa-store text-primary me-2"></i>
                                  <span className="fw-semibold">Collection</span>
                                </div>
                                <div className="mt-2">
                                  <ToggleSwitch
                                    isChecked={formState.compCollectionStatus}
                                    onToggle={(e) => {
                                      setFormState((prev) => ({
                                        ...prev,
                                        compCollectionStatus: e.target?.checked ?? e,
                                      }));
                                    }}
                                    label="Collection"
                                    name="compCollectionStatus"
                                    themeColor={themeColor}
                                  />
                                </div>
                              </div>
                            </div>
                          </div>
                          <div className="col-md-6">
                            <div className="d-flex align-items-center">
                              <div className="flex-fill">
                                <div className="d-flex align-items-center mb-2">
                                  <i className="fas fa-shipping-fast me-2" style={{ color: themeColor }}></i>
                                  <span className="fw-semibold">Delivery</span>
                                </div>
                                <div className="mt-2">
                                  <ToggleSwitch
                                    isChecked={formState.compDeliveryStatus}
                                    onToggle={(e) => {
                                      setFormState((prev) => ({
                                        ...prev,
                                        compDeliveryStatus: e.target?.checked ?? e,
                                      }));
                                    }}
                                    label="Delivery"
                                    name="compDeliveryStatus"
                                    themeColor={themeColor}
                                  />
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>


                      <div className="list-group-item bg-light border-top">
                        <div className="d-flex justify-content-end gap-2 mt-3">
                          <button
                            className="btn btn-outline-secondary btn-sm px-4"
                            onClick={cancelOnlineOrderingEditing}
                            disabled={loading}
                          >
                            <i className="fas fa-times me-1"></i>
                            Cancel
                          </button>
                          <button
                            className="btn btn-sm px-4"
                            style={{
                              backgroundColor: themeColor,
                              color: 'white',
                              border: 'none',
                              transition: 'all 0.3s ease'
                            }}
                            onClick={saveOnlineOrderingSettings}
                            disabled={loading}
                          >
                            {loading ? (
                              <>
                                <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                                Saving...
                              </>
                            ) : (
                              <>
                                <i className="fas fa-save me-1"></i>
                                Save Changes
                              </>
                            )}
                          </button>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <PaymentMethodModal
        isModalOpen={isPaymentModalOpen}
        setIsModalOpen={setIsPaymentModalOpen}
        onPaymentMethodSubmit={handlePaymentMethodSubmit}
        compID={CompID}
      />

      <SubscriptionModal
        isModalOpen={isSubscriptionModalOpen}
        setIsModalOpen={setIsSubscriptionModalOpen}
        onSubscriptionSubmit={handleSubscriptionSubmit}
        compID={CompID}
      />
    </>
  );
}

export default Settings;
